/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Combobox,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Input,
    Label,
    makeStyles,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    MessageBar,
    MessageBarBody,
    Option,
    Select,
    Spinner,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TabList,
    Textarea,
    tokens,
    useId
} from '@fluentui/react-components';
import { AddRegular, ArrowDownloadRegular, ChevronDownRegular, ChevronRightRegular, DeleteRegular, EditRegular, MoreVerticalRegular, SaveRegular } from '@fluentui/react-icons';
import { FileTypeIcon } from '@fluentui/react-icons-file-type';
import React, { useEffect, useMemo, useState } from 'react';
import ExcelExportButton from '../../components/ExcelExportButton';
import { useProject } from '../../context/ProjectContext';
import { Projektrolle } from '../../models/ProjektRolle';
import type { RechnungItem } from '../../models/RechnungItem';
import { DataService } from '../../services/dataService';
import useConfigStore from '../../store/config';
import type { ExcelExportColumn } from '../../utils/excelExport';

const RECHNUNG_STATUS_TEXT: { [key: number]: string } = {
    1: 'Draft',
    2: 'Approved',
    3: 'Sent',
    4: 'Completed',
    5: 'Cancelled',
    6: 'Project Manager Approved',
};

const INVOICE_EXCEL_COLUMNS: ExcelExportColumn<any>[] = [
    { header: 'Company', value: (r) => r.companyTitle || 'Unknown', width: 35 },
    { header: 'Invoice Nr', value: (r) => (r.title ? r.title.split('/')[0] : ''), width: 15 },
    { header: 'Date', value: (r) => r.reDatumAsText, width: 12 },
    { header: 'Contact', value: (r) => r.contactName, width: 25 },
    { header: 'Status', value: (r) => RECHNUNG_STATUS_TEXT[r.status] || r.status, width: 22 },
    { header: 'Gross', value: (r) => r.betragBrutto || 0, width: 14 },
    { header: 'Net', value: (r) => r.betragNetto || 0, width: 14 },
    { header: 'Gross Open', value: (r) => r.betragBruttoOffen || 0, width: 14 },
    { header: 'Net Open', value: (r) => r.betragNettoOffen || 0, width: 14 },
    { header: 'Last Payment', value: (r) => r.datumLetzteZahlungAsText, width: 14 },
    { header: 'Reminders', value: (r) => r.anzahlMahnungen || 0, width: 12 },
    { header: 'Notes', value: (r) => r.bemerkungen, width: 40 },
];

const PARTNER_PAYMENT_EXCEL_COLUMNS: ExcelExportColumn<any>[] = [
    { header: 'Company', value: (z) => z.companyTitle || 'Unknown', width: 35 },
    { header: 'Notes', value: (z) => z.bemerkungen, width: 40 },
    { header: 'Amount', value: (z) => z.betragNetto || 0, width: 14 },
    { header: 'Amount Open', value: (z) => (z.zahlungen?.[0]?.zeDatum === undefined ? z.betragNetto || 0 : 0), width: 14 },
    { header: 'Payment Date', value: (z) => z.zahlungen?.[0]?.zeDatumAsText, width: 14 },
];

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
    },
    tabContent: {
        marginTop: '10px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        overflow: 'auto',
    },
    toolbar: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        marginBottom: tokens.spacingVerticalM,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        flex: 1,
    },
    drawerForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        padding: tokens.spacingVerticalL,
    },
    formSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalL,
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    sectionTitle: {
        fontWeight: 700,
        fontSize: tokens.fontSizeBase500,
        color: tokens.colorBrandForeground1,
        marginBottom: tokens.spacingVerticalS,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
    formLabel: {
        fontWeight: 600,
        fontSize: tokens.fontSizeBase300,
    },
    drawerFooter: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        justifyContent: 'flex-end',
        padding: tokens.spacingVerticalL,
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    checkboxList: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        maxHeight: '200px',
        overflow: 'auto',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        padding: tokens.spacingVerticalS,
        borderRadius: tokens.borderRadiusMedium,
    },
    checkboxItem: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
    },
});

// Reusable Delete Confirmation Dialog Component
interface DeleteConfirmationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    message: string;
    warningMessage?: string;
    onConfirm: () => void | Promise<void>;
    isLoading?: boolean;
    isDangerous?: boolean;
}

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
    open,
    onOpenChange,
    title,
    message,
    warningMessage,
    onConfirm,
    isLoading = false
}) => {
    return (
        <Dialog
            open={open}
            onOpenChange={(_event, data) => {
                if (!data.open) onOpenChange(false);
            }}
        >
            <DialogSurface>
                <DialogContent>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogBody>
                        <div style={{ marginBottom: tokens.spacingVerticalM }}>
                            {message}
                        </div>
                        {warningMessage && (
                            <div style={{ marginBottom: tokens.spacingVerticalM, color: tokens.colorStatusWarningForeground1 }}>
                                {warningMessage}
                            </div>
                        )}
                    </DialogBody>
                    <div style={{
                        display: 'flex',
                        gap: tokens.spacingHorizontalM,
                        justifyContent: 'flex-end',
                        padding: tokens.spacingVerticalL,
                        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
                    }}>
                        <Button
                            appearance="secondary"
                            onClick={() => onOpenChange(false)}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            appearance="primary"
                            onClick={onConfirm}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Deleting...' : 'Delete'}
                        </Button>
                    </div>
                </DialogContent>
            </DialogSurface>
        </Dialog>
    );
};

const Rechnungen: React.FC = () => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const { project, activeRole } = useProject();
    const { selectedRole } = useConfigStore((state) => state.roleConfig);
    const { userInfo } = useConfigStore((state) => state.userConfig);

    // Permission check for editing invoices
    const canEditInvoices = useMemo(() => {
        // Check if user has Controlling, Rechnungswesen (Accounting), or Administratoren role
        const hasGlobalRole = selectedRole === 'Controlling' ||
            selectedRole === 'Rechnungswesen' ||
            selectedRole === 'Accounting' ||
            selectedRole === 'Administratoren';

        if (hasGlobalRole) return true;

        // Check if user is project manager (ProjektleiterId == userId)
        if (project && userInfo && project.projektleiterID === userInfo.userId) return true;

        // Check if user has Projektleiterassistent role (activeRole == 2)
        if (activeRole === Projektrolle.Projektleiterassistent) return true;

        return false;
    }, [selectedRole, project, userInfo, activeRole]);

    // Permission check for downloading invoices (only Controlling and Administratoren)
    const canDownloadRechnungen = useMemo(() => {
        return selectedRole === 'Controlling' || selectedRole === 'Administratoren';
    }, [selectedRole]);

    // Permission check for approving invoices (Projektleiter, Projektleiterassistent, Controlling, Administratoren)
    const canApproveRechnungen = useMemo(() => {
        const hasGlobalRole = selectedRole === 'Controlling' || selectedRole === 'Administratoren';
        if (hasGlobalRole) return true;

        // Check if user is project manager (Projektleiter)
        if (project && userInfo && project.projektleiterID === userInfo.userId) return true;

        // Check if user has Projektleiterassistent role
        if (activeRole === Projektrolle.Projektleiterassistent) return true;

        return false;
    }, [selectedRole, project, userInfo, activeRole]);

    // Create IDs for Select components
    const versandartSelectId = useId();
    const rechnungsspracheSelectId = useId();
    const zahlungsfristSelectId = useId();
    const gegenzeichnung1SelectId = useId();
    const gegenzeichnung2SelectId = useId();
    const teilbetragUStSatzSelectId = useId();
    const kontoSelectId = useId();

    const [rechnungen, setRechnungen] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingRechnung, setEditingRechnung] = useState<any | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [expandedCompanies, setExpandedCompanies] = useState<Set<number>>(new Set());
    const [isDownloading, setIsDownloading] = useState(false);
    const [messageBarVisible, setMessageBarVisible] = useState(false);
    const [messageBarContent, setMessageBarContent] = useState<string>('');
    const messageBarTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // Form state
    const [projektpartnerList, setProjektpartnerList] = useState<any[]>([]);
    const [selectedProjektpartner, setSelectedProjektpartner] = useState<any | null>(null);
    const [kontaktpersonList, setKontaktpersonList] = useState<any[]>([]);
    const [selectedKontaktperson, setSelectedKontaktperson] = useState<any | null>(null);

    // Rechnungsempfänger section
    const [versandart, setVersandart] = useState<string>('Post');
    const [versandEmail, setVersandEmail] = useState<string>('');

    // Rechnungsdaten section
    const [rechnungsNr, setRechnungsNr] = useState<string>('');
    const [rechnungsDatum, setRechnungsDatum] = useState<string>('');
    const [rechnungssprache, setRechnungssprache] = useState<string>('0');
    const [leistungszeitraum, setLeistungszeitraum] = useState<string>('');
    const [zahlungsfristId, setZahlungsfristId] = useState<string>('1');
    const [kontoId, setKontoId] = useState<string>('1');
    const [kontoList, setKontoList] = useState<any[]>([]);

    // Teilbeträge section
    const [teilbeträge, setTeilbeträge] = useState<any[]>([]);
    //const [zahlungen, setZahlungen] = useState<any[]>([]);
    const [zahlungsplan, setZahlungsplan] = useState<any[]>([]);

    // Zusatzinformation section
    const [bemerkungen, setBemerkungen] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [mahnungen, setMahnungen] = useState<string>('');
    const [benutzerFreigabeAngefordert, setBenutzerFreigabeAngefordert] = useState<boolean>(false);

    // Countersignatures section
    const [gegenzeichnung1Id, setGegenzeichnung1Id] = useState<string>('');
    const [gegenzeichnung2Id, setGegenzeichnung2Id] = useState<string>('');

    // Zahlungstermine section
    const [selectedZahlungstermineIds, setSelectedZahlungstermineIds] = useState<Set<number>>(new Set());

    // Teilbetrag editing
    const [isTeilbetragDrawerOpen, setIsTeilbetragDrawerOpen] = useState(false);
    const [editingTeilbetrag, setEditingTeilbetrag] = useState<any | null>(null);
    const [isTeilbetragEditMode, setIsTeilbetragEditMode] = useState(false);
    const [teilbetragLeistung, setTeilbetragLeistung] = useState<string>('');
    const [teilbetragBetragNetto, setTeilbetragBetragNetto] = useState<string>('');
    const [teilbetragUStSatz, setTeilbetragUStSatz] = useState<string>('20');
    const [teilbetragWerbeAbgabe, setTeilbetragWerbeAbgabe] = useState<boolean>(false);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [teilbetragToDelete, setTeilbetragToDelete] = useState<any | null>(null);
    const [approvalConfirmationOpen, setApprovalConfirmationOpen] = useState(false);
    const [rechnungToApprove, setRechnungToApprove] = useState<any | null>(null);

    // Partnerzahlungen state
    const [partnerzahlungen, setPartnerzahlungen] = useState<any[]>([]);
    const [expandedPartnerCompanies, setExpandedPartnerCompanies] = useState<Set<number>>(new Set());
    const [isPartnerzahlungDrawerOpen, setIsPartnerzahlungDrawerOpen] = useState(false);
    const [isPartnerzahlungEditMode, setIsPartnerzahlungEditMode] = useState(false);
    const [editingPartnerzahlung, setEditingPartnerzahlung] = useState<any | null>(null);
    const [isSavingPartnerzahlung, setIsSavingPartnerzahlung] = useState(false);

    // Partnerzahlung form state
    const [partnerzahlungBetrag, setPartnerzahlungBetrag] = useState<string>('');
    const [partnerzahlungIban, setPartnerzahlungIban] = useState<string>('');
    const [partnerzahlungBetreff, setPartnerzahlungBetreff] = useState<string>('');
    const [partnerzahlungZahlungsdatum, setPartnerzahlungZahlungsdatum] = useState<string>('');
    const [partnerzahlungAnhangName, setPartnerzahlungAnhangName] = useState<string>('');
    const [partnerzahlungZahlungsnachweisName, setPartnerzahlungZahlungsnachweisName] = useState<string>('');
    const [partnerzahlungAnhang, setPartnerzahlungAnhang] = useState<File | null>(null);
    const [partnerzahlungZahlungsnachweis, setPartnerzahlungZahlungsnachweis] = useState<File | null>(null);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    // Zahlungseingänge state
    const [isZahlungseingaengeDrawerOpen, setIsZahlungseingaengeDrawerOpen] = useState(false);
    const [selectedRechnungForZahlungen, setSelectedRechnungForZahlungen] = useState<RechnungItem | null>(null);

    // Zahlungseingang drawer state
    const [isZahlungseingangDrawerOpen, setIsZahlungseingangDrawerOpen] = useState(false);
    const [isZahlungseingangEditMode, setIsZahlungseingangEditMode] = useState(false);
    const [editingZahlungseingang, setEditingZahlungseingang] = useState<any | null>(null);
    const [isSavingZahlungseingang, setIsSavingZahlungseingang] = useState(false);
    const [zahlungseingangDatum, setZahlungseingangDatum] = useState<string>('');
    const [zahlungseingangBemerkungen, setZahlungseingangBemerkungen] = useState<string>('');
    const [zahlungseingangBetrag, setZahlungseingangBetrag] = useState<string>('');
    const [zahlungseingangTeilbetraege, setZahlungseingangTeilbetraege] = useState<any[]>([]);

    // Zahlungseingang Teilbetrag drawer state
    const [isZahlungseingangTeilbetragDrawerOpen, setIsZahlungseingangTeilbetragDrawerOpen] = useState<boolean>(false);
    const [isZahlungseingangTeilbetragEditMode, setIsZahlungseingangTeilbetragEditMode] = useState<boolean>(false);
    const [editingZahlungseingangTeilbetrag, setEditingZahlungseingangTeilbetrag] = useState<any>(null);
    const [zahlungseingangTeilbetragTeilbetragId, setZahlungseingangTeilbetragTeilbetragId] = useState<number | null>(null);
    const [zahlungseingangTeilbetragBetragNetto, setZahlungseingangTeilbetragBetragNetto] = useState<string>('');
    const [zahlungseingangTeilbetragBetragBrutto, setZahlungseingangTeilbetragBetragBrutto] = useState<string>('');

    // Message state for Zahlungseinggang Teilbetrag operations
    const [zahlungseingangTeilbetragMessage, setZahlungseingangTeilbetragMessage] = useState<string>('');
    const [zahlungseingangTeilbetragMessageType, setZahlungseingangTeilbetragMessageType] = useState<'success' | 'error'>('success');
    const [zahlungseingangTeilbetragMessageVisible, setZahlungseingangTeilbetragMessageVisible] = useState(false);
    const zahlungseingangTeilbetragMessageTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // Message state for Zahlungseingänge parent drawer (management drawer)
    const [zahlungseingaengeParentMessage, setZahlungseingaengeParentMessage] = useState<string>('');
    const [zahlungseingaengeParentMessageType, setZahlungseingaengeParentMessageType] = useState<'success' | 'error'>('success');
    const [zahlungseingaengeParentMessageVisible, setZahlungseingaengeParentMessageVisible] = useState(false);
    const zahlungseingaengeParentMessageTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // Delete confirmation dialog for payment receipt
    const [deleteZahlungseingangConfirmationOpen, setDeleteZahlungseingangConfirmationOpen] = useState(false);
    const [zahlungseingangToDelete, setZahlungseingangToDelete] = useState<any | null>(null);

    // Delete confirmation dialog for payment partial amount
    const [deleteZahlungseingangTeilbetragConfirmationOpen, setDeleteZahlungseingangTeilbetragConfirmationOpen] = useState(false);
    const [zahlungseingangTeilbetragToDelete, setZahlungseingangTeilbetragToDelete] = useState<any | null>(null);

    // Check if all required fields are filled
    const isFormValid = useMemo(() => {
        return (
            selectedProjektpartner !== null &&
            selectedKontaktperson !== null &&
            versandart !== '' &&
            (versandart !== 'E-Mail' || (versandart === 'E-Mail' && versandEmail.trim() !== '')) &&
            rechnungssprache !== '0' &&
            leistungszeitraum.trim() !== '' &&
            kontoId !== '' && kontoId !== '0' &&
            zahlungsfristId !== '' && zahlungsfristId !== '0' &&
            gegenzeichnung1Id !== '' &&
            mahnungen !== ''
        );
    }, [selectedProjektpartner, selectedKontaktperson, versandart, versandEmail, rechnungssprache, leistungszeitraum, kontoId, zahlungsfristId, gegenzeichnung1Id, mahnungen]);

    // Hardcoded countersignatures list
    const countersignatures = [
        { id: 523, name: 'BAMBERGER Christa' },
        { id: 4544, name: 'BRÄNDLE Udo' },
        { id: 17, name: 'PROMMER Ulrike' },
        { id: 2143, name: 'PRUHS Hans Georg' },
        { id: 552, name: 'SABATHIEL Michaela' },
    ];

    // Helper function for European currency formatting
    const formatEuropeanCurrency = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return '€ 0,00';
        const fixed = value.toFixed(2);
        const [integer, decimal] = fixed.split('.');
        const withThousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return `€ ${withThousands},${decimal}`;
    };

    // Helper function to show Zahlungseinggang Teilbetrag message with auto-hide
    const showZahlungseingangTeilbetragMessage = (message: string, type: 'success' | 'error' = 'success') => {
        if (zahlungseingangTeilbetragMessageTimeoutRef.current) {
            clearTimeout(zahlungseingangTeilbetragMessageTimeoutRef.current);
        }
        setZahlungseingangTeilbetragMessage(message);
        setZahlungseingangTeilbetragMessageType(type);
        setZahlungseingangTeilbetragMessageVisible(true);
        zahlungseingangTeilbetragMessageTimeoutRef.current = setTimeout(() => {
            setZahlungseingangTeilbetragMessageVisible(false);
        }, 5000);
    };

    // Helper function to show Zahlungseingänge parent drawer message with auto-hide
    const showZahlungseingaengeParentMessage = (message: string, type: 'success' | 'error' = 'success') => {
        if (zahlungseingaengeParentMessageTimeoutRef.current) {
            clearTimeout(zahlungseingaengeParentMessageTimeoutRef.current);
        }
        setZahlungseingaengeParentMessage(message);
        setZahlungseingaengeParentMessageType(type);
        setZahlungseingaengeParentMessageVisible(true);
        zahlungseingaengeParentMessageTimeoutRef.current = setTimeout(() => {
            setZahlungseingaengeParentMessageVisible(false);
        }, 5000);
    };

    // Handler to open Zahlungseingänge drawer
    const handleManageZahlungseingaenge = (rechnung: any) => {
        console.log('Managing Zahlungseingänge for Rechnung:', rechnung);
        setSelectedRechnungForZahlungen(rechnung);
        setIsZahlungseingaengeDrawerOpen(true);
    };

    // Handler to close Zahlungseingänge drawer
    const handleZahlungseingaengeDrawerClose = () => {
        setIsZahlungseingaengeDrawerOpen(false);
        setSelectedRechnungForZahlungen(null);
    };

    // Handler to add Zahlungseingang
    const handleAddZahlungseingang = (rechnung: any) => {
        console.log('Adding Zahlungseingang for Rechnung:', rechnung);
        setEditingZahlungseingang(null);
        setIsZahlungseingangEditMode(false);
        setZahlungseingangDatum('');
        setZahlungseingangBemerkungen('');
        setZahlungseingangBetrag('');
        setZahlungseingangTeilbetraege([]);
        setSelectedRechnungForZahlungen(rechnung);
        setIsZahlungseingangDrawerOpen(true);
    };

    // Handler to edit Zahlungseingang
    const handleEditZahlungseingang = async (zahlung: any) => {
        try {
            console.log('Editing Zahlungseingang:', zahlung);

            // Use the data from zahlung directly - it already has all the info we need
            setEditingZahlungseingang(zahlung);
            setIsZahlungseingangEditMode(true);
            setZahlungseingangDatum(zahlung?.zeDatum ? zahlung.zeDatum.toString().substring(0, 10) : '');
            setZahlungseingangBemerkungen(zahlung?.bemerkungen || '');
            setZahlungseingangBetrag(zahlung?.betragNetto ? zahlung.betragNetto.toString() : '');
            setZahlungseingangTeilbetraege(zahlung?.zahlungTeilbetraege || []);
            setIsZahlungseingangDrawerOpen(true);
        } catch (err) {
            console.error('Error preparing Zahlungseingang for edit:', err);
            alert(`Error loading payment receipt: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    };

    // Handler to close Zahlungseingang drawer
    const handleZahlungseingangDrawerClose = () => {
        setIsZahlungseingangDrawerOpen(false);
        setEditingZahlungseingang(null);
        setIsZahlungseingangEditMode(false);
        setZahlungseingangDatum('');
        setZahlungseingangBemerkungen('');
        setZahlungseingangBetrag('');
        setZahlungseingangTeilbetraege([]);
    };

    // Handler to save Zahlungseingang
    const handleSaveZahlungseingang = async () => {
        if (!dataService || !selectedRechnungForZahlungen?.rechnungID || !project?.projektID) {
            alert('No invoice selected');
            return;
        }

        try {
            setIsSavingZahlungseingang(true);

            const zahlungData = {
                itemId: editingZahlungseingang?.itemId || 0,
                rechnungId: selectedRechnungForZahlungen.rechnungID,
                zeDatum: zahlungseingangDatum,
                bemerkungen: zahlungseingangBemerkungen,
                betragNetto: parseFloat(zahlungseingangBetrag) || 0,
                zahlungTeilbetraege: zahlungseingangTeilbetraege,
            };

            if (isZahlungseingangEditMode && editingZahlungseingang?.itemId) {
                // Update existing
                await dataService.updateZahlungRechnung(zahlungData);
            } else {
                // Add new
                await dataService.addZahlungRechnung(zahlungData);
            }

            // Refresh the single invoice data to update the Zahlungen list
            const fullRechnung = await dataService.getRechnung(selectedRechnungForZahlungen.rechnungID);
            setSelectedRechnungForZahlungen(fullRechnung);

            // Refresh the main invoices list to keep it in sync
            const data = await dataService.getRechnungenListe(project.projektID);
            setRechnungen(data || []);

            showZahlungseingaengeParentMessage('Payment receipt saved successfully!', 'success');
            // Close the child drawer after a short delay so the message is visible in the parent drawer
            setTimeout(() => {
                handleZahlungseingangDrawerClose();
            }, 300);
        } catch (err) {
            console.error('Error saving Zahlungseingang:', err);
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            showZahlungseingaengeParentMessage(`Error saving payment receipt: ${errorMessage}`, 'error');
            setTimeout(() => {
                handleZahlungseingangDrawerClose();
            }, 300);
        } finally {
            setIsSavingZahlungseingang(false);
        }
    };

    // Handler to delete Zahlungseingang
    const handleDeleteZahlungseingang = (zahlung: any) => {
        setZahlungseingangToDelete(zahlung);
        setDeleteZahlungseingangConfirmationOpen(true);
    };

    // Handler to confirm deletion of Zahlungseingang
    const handleConfirmDeleteZahlungseingang = async () => {
        if (!dataService || !selectedRechnungForZahlungen?.rechnungID || !project?.projektID || !zahlungseingangToDelete) {
            setDeleteZahlungseingangConfirmationOpen(false);
            return;
        }

        try {
            setIsSavingZahlungseingang(true);
            await dataService.deleteZahlungRechnung(zahlungseingangToDelete.itemId);
            // Refresh the single invoice data
            const fullRechnung = await dataService.getRechnung(selectedRechnungForZahlungen.rechnungID);
            setSelectedRechnungForZahlungen(fullRechnung);

            // Refresh the main invoices list to keep it in sync
            const data = await dataService.getRechnungenListe(project.projektID);
            setRechnungen(data || []);
            showZahlungseingaengeParentMessage('Payment receipt deleted successfully!', 'success');
        } catch (err) {
            console.error('Error deleting Zahlungseingang:', err);
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            showZahlungseingaengeParentMessage(`Error deleting payment receipt: ${errorMessage}`, 'error');
        } finally {
            setIsSavingZahlungseingang(false);
            setDeleteZahlungseingangConfirmationOpen(false);
            setZahlungseingangToDelete(null);
        }
    };

    // Handler to add Zahlungseingang Teilbetrag
    const handleAddZahlungseingangTeilbetrag = () => {
        setEditingZahlungseingangTeilbetrag(null);
        setIsZahlungseingangTeilbetragEditMode(false);
        setZahlungseingangTeilbetragTeilbetragId(null);
        setZahlungseingangTeilbetragBetragNetto('');
        setZahlungseingangTeilbetragBetragBrutto('');
        setIsZahlungseingangTeilbetragDrawerOpen(true);
    };

    // Handler to edit Zahlungseingang Teilbetrag
    const handleEditZahlungseingangTeilbetrag = (teilbetrag: any) => {
        setEditingZahlungseingangTeilbetrag(teilbetrag);
        setIsZahlungseingangTeilbetragEditMode(true);
        setZahlungseingangTeilbetragTeilbetragId(teilbetrag.teilbetragId);
        setZahlungseingangTeilbetragBetragNetto(teilbetrag.betragNetto?.toString() || '');
        setZahlungseingangTeilbetragBetragBrutto(teilbetrag.betragBrutto?.toString() || '');
        setIsZahlungseingangTeilbetragDrawerOpen(true);
    };

    // Handler to close Zahlungseingang Teilbetrag drawer
    const handleCloseZahlungseingangTeilbetragDrawer = () => {
        setIsZahlungseingangTeilbetragDrawerOpen(false);
        setEditingZahlungseingangTeilbetrag(null);
        setIsZahlungseingangTeilbetragEditMode(false);
        setZahlungseingangTeilbetragTeilbetragId(null);
        setZahlungseingangTeilbetragBetragNetto('');
        setZahlungseingangTeilbetragBetragBrutto('');
    };

    // Handler to save Zahlungseingang Teilbetrag
    const handleSaveZahlungseingangTeilbetrag = () => {
        if (!zahlungseingangTeilbetragTeilbetragId || !zahlungseingangTeilbetragBetragNetto) {
            showZahlungseingangTeilbetragMessage('Please fill in all required fields', 'error');
            return;
        }

        if (isZahlungseingangTeilbetragEditMode && editingZahlungseingangTeilbetrag) {
            // Update existing
            const updatedTeilbetraege = zahlungseingangTeilbetraege.map((tb: any) =>
                tb.itemId === editingZahlungseingangTeilbetrag.itemId
                    ? {
                        ...tb,
                        teilbetragId: zahlungseingangTeilbetragTeilbetragId,
                        betragNetto: parseFloat(zahlungseingangTeilbetragBetragNetto) || 0,
                        betragBrutto: parseFloat(zahlungseingangTeilbetragBetragBrutto) || 0,
                    }
                    : tb
            );
            setZahlungseingangTeilbetraege(updatedTeilbetraege);
            showZahlungseingangTeilbetragMessage('Payment amount updated successfully!', 'success');
        } else {
            // Add new
            const newTeilbetrag = {
                zahlungId: editingZahlungseingang?.itemId || 0,
                teilbetragId: zahlungseingangTeilbetragTeilbetragId,
                itemId: 0, // Will be assigned by backend
                betragNetto: parseFloat(zahlungseingangTeilbetragBetragNetto) || 0,
                betragBrutto: parseFloat(zahlungseingangTeilbetragBetragBrutto) || 0,
                isNettoSet: true,
                isBruttoSet: false,
            };
            setZahlungseingangTeilbetraege([...zahlungseingangTeilbetraege, newTeilbetrag]);
            showZahlungseingangTeilbetragMessage('Payment amount added successfully!', 'success');
        }

        handleCloseZahlungseingangTeilbetragDrawer();
    };

    // Handler to delete Zahlungseingang Teilbetrag
    const handleDeleteZahlungseingangTeilbetrag = (teilbetrag: any) => {
        setZahlungseingangTeilbetragToDelete(teilbetrag);
        setDeleteZahlungseingangTeilbetragConfirmationOpen(true);
    };

    // Handler to confirm deletion of Zahlungseingang Teilbetrag
    const handleConfirmDeleteZahlungseingangTeilbetrag = async () => {
        if (!zahlungseingangTeilbetragToDelete) {
            setDeleteZahlungseingangTeilbetragConfirmationOpen(false);
            return;
        }

        const updatedTeilbetraege = zahlungseingangTeilbetraege.filter(
            (tb: any) => tb.itemId !== zahlungseingangTeilbetragToDelete.itemId
        );
        setZahlungseingangTeilbetraege(updatedTeilbetraege);
        showZahlungseingangTeilbetragMessage('Payment amount removed successfully!', 'success');
        setDeleteZahlungseingangTeilbetragConfirmationOpen(false);
        setZahlungseingangTeilbetragToDelete(null);
    };

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch Rechnungen on mount
    useEffect(() => {
        const fetchRechnungen = async () => {
            if (!dataService || !project?.projektID) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const data = await dataService.getRechnungenListe(project.projektID);
                console.log('Fetched invoices:', data);
                setRechnungen(data || []);

                // Auto-expand all company groups
                const companyIds = new Set<number>();
                (data || []).forEach((rechnung: any) => {
                    if (rechnung.companyID) {
                        companyIds.add(rechnung.companyID);
                    }
                });
                setExpandedCompanies(companyIds);
            } catch (err) {
                console.error('Error fetching invoices:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRechnungen();
    }, [dataService, project?.projektID]);

    // Fetch Partnerzahlungen on mount
    useEffect(() => {
        const fetchPartnerzahlungen = async () => {
            if (!dataService || !project?.projektID) {
                return;
            }

            try {
                const data = await dataService.getPartnerzahlungenListe(project.projektID);
                setPartnerzahlungen(data || []);

                // Auto-expand all company groups
                const companyIds = new Set<number>();
                (data || []).forEach((zahlung: any) => {
                    if (zahlung.companyID) {
                        companyIds.add(zahlung.companyID);
                    }
                });
                setExpandedPartnerCompanies(companyIds);
            } catch (err) {
                console.error('Error fetching partner payments:', err);
            }
        };

        fetchPartnerzahlungen();
    }, [dataService, project?.projektID]);

    // Fetch Projektpartner for combobox
    useEffect(() => {
        const fetchProjektpartner = async () => {
            if (!dataService || !project?.projektID) return;

            try {
                const partners = await dataService.getProjektpartnerListeAlleAktiven(
                    project.projektID
                );
                setProjektpartnerList(partners || []);
            } catch (err) {
                console.error('Error fetching project partners:', err);
            }
        };

        fetchProjektpartner();
    }, [dataService, project?.projektID, isDrawerOpen]);

    // Fetch Kontaktperson when Projektpartner changes
    useEffect(() => {
        const fetchKontaktperson = async () => {
            if (!dataService || !selectedProjektpartner?.partnerID) {
                setKontaktpersonList([]);
                setSelectedKontaktperson(null);
                return;
            }

            try {
                const contacts = await dataService.getProjektKontakte(
                    selectedProjektpartner.partnerID
                );
                setKontaktpersonList(contacts || []);

                // If we're in edit mode and have a contactID, select the matching contact
                let contactID: number | undefined;
                if (isEditMode && editingRechnung?.contactID) {
                    contactID = editingRechnung.contactID;
                } else if (isPartnerzahlungEditMode && editingPartnerzahlung?.contactID) {
                    contactID = editingPartnerzahlung.contactID;
                }

                if (contactID) {
                    const matchingContact = (contacts || []).find((c: any) =>
                        c.contactID === contactID || c.id === contactID
                    );
                    if (matchingContact) {
                        setSelectedKontaktperson(matchingContact);
                    }
                }
            } catch (err) {
                console.error('Error fetching contacts:', err);
            }
        };

        fetchKontaktperson();
    }, [dataService, selectedProjektpartner?.partnerID, isEditMode, editingRechnung?.contactID, isPartnerzahlungEditMode, editingPartnerzahlung?.contactID]);

    const handleAddClick = () => {
        setEditingRechnung(null);
        setIsEditMode(false);
        setRechnungsNr('');
        setRechnungsDatum('');
        setRechnungssprache('0');
        setLeistungszeitraum('');
        setZahlungsfristId('1');
        setVersandart('Post');
        setVersandEmail('');
        setBemerkungen('');
        setStatus('');
        setMahnungen('0');
        setBenutzerFreigabeAngefordert(false);
        setTeilbeträge([]);
        setSelectedProjektpartner(null);
        setSelectedKontaktperson(null);
        setGegenzeichnung1Id('');
        setGegenzeichnung2Id('');
        setSelectedZahlungstermineIds(new Set());
        setKontoId('1');
        setKontoList([]);
        setIsDrawerOpen(true);
    };

    // Handle download invoice in specified format
    const handleDownloadRechnung = async (rechnung: RechnungItem, typePdf: boolean, customHeader?: string) => {
        try {
            setIsDownloading(true);
            const dataService = new DataService(instance, accounts[0]);
            // Use rechnungID as guid and title/reNummer as header
            const rechnungGuid = String(rechnung.guid);
            const rechnungHeader = customHeader || rechnung.title || rechnung.reNummer || `Rechnung ${rechnung.rechnungID}`;

            // Download in specified format
            const { blob, filename } = await dataService.getProjektrechnungBinaries(rechnungGuid, rechnungHeader, typePdf);

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading invoice:', err);
            alert(`Error downloading invoice: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleImportRechnungToDocuments = async (rechnung: RechnungItem) => {
        try {
            setIsDownloading(true);
            const dataService = new DataService(instance, accounts[0]);
            await dataService.importRechnungToDocuments(rechnung.rechnungID);
            setMessageBarContent('Invoice imported to documents successfully');
            setMessageBarVisible(true);
            // Clear previous timeout if exists
            if (messageBarTimeoutRef.current) {
                clearTimeout(messageBarTimeoutRef.current);
            }
            // Hide message bar after 5 seconds
            messageBarTimeoutRef.current = setTimeout(() => {
                setMessageBarVisible(false);
            }, 5000);
        } catch (err) {
            console.error('Error importing invoice to documents:', err);
            setMessageBarContent(`Error importing invoice to documents: ${err instanceof Error ? err.message : 'Unknown error'}`);
            setMessageBarVisible(true);
            // Clear previous timeout if exists
            if (messageBarTimeoutRef.current) {
                clearTimeout(messageBarTimeoutRef.current);
            }
            // Hide message bar after 5 seconds
            messageBarTimeoutRef.current = setTimeout(() => {
                setMessageBarVisible(false);
            }, 5000);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleEditClick = async (rechnung: any) => {
        try {
            const fullRechnung = await dataService?.getRechnung(rechnung.rechnungID);
            console.log("Full Rechnung", fullRechnung);
            setEditingRechnung(fullRechnung);
            setIsEditMode(true);

            // Populate form fields with correct API field names
            setRechnungsNr(fullRechnung?.reNummer || '');
            // Extract date in YYYY-MM-DD format for input type="date" (no Date conversion to avoid timezone issues)
            setRechnungsDatum(fullRechnung?.reDatum ? fullRechnung.reDatum.toString().substring(0, 10) : '');
            setRechnungssprache(fullRechnung?.rechungSprache?.toString() || '0');
            setLeistungszeitraum(fullRechnung?.zeitraum || '');
            setZahlungsfristId(fullRechnung?.zahlungsfristId?.toString() || '1');
            setVersandart(fullRechnung?.versandArt || 'Post');
            setVersandEmail(fullRechnung?.empfaengerEmail || '');
            setBemerkungen(fullRechnung?.bemerkungen || '');
            setStatus(fullRechnung?.status?.toString() || '0');
            setMahnungen(fullRechnung?.anzahlMahnungen?.toString() || '0');
            setBenutzerFreigabeAngefordert(fullRechnung?.benutzerFreigabeAngefordert === 1 ? true : false);

            // Set Konto
            if (fullRechnung?.kontoId) {
                setKontoId(fullRechnung.kontoId.toString());
                // Create konto list from current rechnung's konto data
                const konto = {
                    kontoId: fullRechnung.kontoId,
                    kontoBankName: fullRechnung.kontoBankName,
                    kontoBlz: fullRechnung.kontoBlz,
                    kontoNummer: fullRechnung.kontoNummer,
                };
                setKontoList([konto]);
            } else {
                setKontoId('1');
                setKontoList([]);
            }
            setTeilbeträge(fullRechnung?.teilbetraege || []);
            console.log("Teilbeträge", fullRechnung?.teilbetraege);
            //setZahlungen(fullRechnung?.zahlungen || []);
            setZahlungsplan(fullRechnung?.zahlungstermine || []);
            // Set selected Zahlungstermine IDs
            if (fullRechnung?.zahlungstermine && fullRechnung.zahlungstermine.length > 0) {
                const ids = new Set(fullRechnung.zahlungstermine.map((z: any) => z.id || z.zahlungsplanId));
                setSelectedZahlungstermineIds(ids);
            } else {
                setSelectedZahlungstermineIds(new Set());
            }
            setGegenzeichnung1Id(fullRechnung?.gegenzeichnung1Id?.toString() || '');
            setGegenzeichnung2Id(fullRechnung?.gegenzeichnung2Id?.toString() || '');

            // Set the selected partner using companyID
            if (fullRechnung?.companyID) {
                const partner = projektpartnerList.find(
                    (p) => p.partnerID === fullRechnung.companyID
                );
                setSelectedProjektpartner(partner || null);
            }

            setIsDrawerOpen(true);
        } catch (err) {
            console.error('Error loading invoice:', err);
        }
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setEditingRechnung(null);
        setIsEditMode(false);
        setSelectedProjektpartner(null);
        setSelectedKontaktperson(null);
        setRechnungsNr('');
        setRechnungsDatum('');
        setRechnungssprache('0');
        setLeistungszeitraum('');
        setZahlungsfristId('1');
        setVersandart('Post');
        setVersandEmail('');
        setBemerkungen('');
        setStatus('');
        setMahnungen('');
        setBenutzerFreigabeAngefordert(false);
        setTeilbeträge([]);
        setGegenzeichnung1Id('');
        setGegenzeichnung2Id('');
        setSelectedZahlungstermineIds(new Set());
        setKontoId('1');
        setKontoList([]);
        setValidationErrors([]);
    };

    const toggleCompanyExpand = (companyId: number) => {
        setExpandedCompanies((prev) => {
            const updated = new Set(prev);
            if (updated.has(companyId)) {
                updated.delete(companyId);
            } else {
                updated.add(companyId);
            }
            return updated;
        });
    };

    // Teilbetrag handlers
    const handleAddTeilbetrag = () => {
        setEditingTeilbetrag(null);
        setIsTeilbetragEditMode(false);
        setTeilbetragLeistung('');
        setTeilbetragBetragNetto('');
        setTeilbetragUStSatz('20');
        setTeilbetragWerbeAbgabe(false);
        setIsTeilbetragDrawerOpen(true);
    };

    const handleEditTeilbetrag = (teilbetrag: any) => {
        setEditingTeilbetrag(teilbetrag);
        setIsTeilbetragEditMode(true);
        setTeilbetragLeistung(teilbetrag.leistung || '');
        setTeilbetragBetragNetto(teilbetrag.betragNetto?.toString() || '');
        setTeilbetragUStSatz(teilbetrag.uStSatz?.toString() || '20');
        // Check if werbeAbgabe is non-zero (true = Yes, false = No)
        setTeilbetragWerbeAbgabe(teilbetrag.werbeAbgabe && teilbetrag.werbeAbgabe !== 0);
        setIsTeilbetragDrawerOpen(true);
    };

    const handleDeleteTeilbetrag = (teilbetrag: any) => {
        setTeilbetragToDelete(teilbetrag);
        setDeleteConfirmationOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!dataService || !teilbetragToDelete || !editingRechnung) return;

        try {
            await dataService.deleteTeilbetragRechnung(teilbetragToDelete.itemId);
            // Remove from local state
            const updated = teilbeträge.filter((t: any) => t.itemId !== teilbetragToDelete.itemId);
            setTeilbeträge(updated);
            setDeleteConfirmationOpen(false);
            setTeilbetragToDelete(null);
        } catch (err) {
            console.error('Error deleting Teilbetrag:', err);
        }
    };

    const handleApproveRechnung = (rechnung: any) => {
        setRechnungToApprove(rechnung);
        setApprovalConfirmationOpen(true);
    };

    const handleConfirmApprove = async () => {
        if (!dataService || !rechnungToApprove) return;

        try {
            await dataService.freigabeRechnung(rechnungToApprove.rechnungID);
            // Refresh list
            const data = await dataService.getRechnungenListe(project?.projektID || 0);
            setRechnungen(data || []);
            setApprovalConfirmationOpen(false);
            setRechnungToApprove(null);
        } catch (err) {
            console.error('Error approving invoice:', err);
        }
    };

    const handleTeilbetragDrawerClose = () => {
        setIsTeilbetragDrawerOpen(false);
        setEditingTeilbetrag(null);
        setIsTeilbetragEditMode(false);
        setTeilbetragLeistung('');
        setTeilbetragBetragNetto('');
        setTeilbetragUStSatz('20');
        setTeilbetragWerbeAbgabe(false);
    };

    const handleSaveTeilbetrag = async () => {
        if (!dataService || !editingRechnung) {
            alert('Please save the invoice first before adding partial amounts');
            return;
        }

        try {
            console.log("Saving Teilbetrag", { teilbetragLeistung, teilbetragBetragNetto, teilbetragUStSatz, teilbetragWerbeAbgabe });
            const teilbetragData = {
                rechnungID: editingRechnung.rechnungID,
                leistung: teilbetragLeistung,
                betragNetto: parseFloat(teilbetragBetragNetto) || 0,
                uStSatz: parseInt(teilbetragUStSatz),
                werbeAbgabe: teilbetragWerbeAbgabe ? 5 : 0,
            };

            if (isTeilbetragEditMode && editingTeilbetrag?.itemId) {
                // Update existing
                await dataService.updateTeilbetragRechnung({
                    itemId: editingTeilbetrag.itemId,
                    ...teilbetragData
                });
            } else {
                // Add new
                await dataService.addTeilbetragRechnung(teilbetragData);
            }

            // Refresh the full invoice data to get all Teilbeträge with calculated fields
            const fullRechnung = await dataService.getRechnung(editingRechnung.rechnungID);
            setTeilbeträge(fullRechnung?.teilbetraege || []);

            handleTeilbetragDrawerClose();
        } catch (err) {
            console.error('Error saving Teilbetrag:', err);
        }
    };

    const handleSave = async () => {
        if (!dataService || !project?.projektID) return;

        // Validate required fields
        const errors: string[] = [];

        if (!selectedProjektpartner) {
            errors.push('Projektpartner is required');
        }
        if (!selectedKontaktperson) {
            errors.push('Kontaktperson is required');
        }
        if (!versandart) {
            errors.push('Versandart is required');
        }
        if (versandart === 'E-Mail' && !versandEmail.trim()) {
            errors.push('Email Address is required when Versandart is E-Mail');
        }
        if (rechnungssprache === '0') {
            errors.push('Rechnungssprache is required');
        }
        if (!leistungszeitraum.trim()) {
            errors.push('Leistungszeitraum is required');
        }
        if (!kontoId || kontoId === '0') {
            errors.push('Konto is required');
        }
        if (!zahlungsfristId || zahlungsfristId === '0') {
            errors.push('Zahlungsfrist is required');
        }
        if (!gegenzeichnung1Id) {
            errors.push('Gegenzeichnung 1 is required');
        }
        if (!mahnungen) {
            errors.push('Mahnungen is required');
        }

        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }

        setValidationErrors([]);

        try {
            setIsSaving(true);

            const rechnungData = {
                rechnungID: editingRechnung?.rechnungID || 0,
                projektID: project.projektID,
                reNummer: rechnungsNr,
                reDatum: rechnungsDatum,
                rechungSprache: parseInt(rechnungssprache),
                zeitraum: leistungszeitraum,
                zahlungsfristId: parseInt(zahlungsfristId),
                versandArt: versandart,
                empfaengerEmail: versandart === 'E-Mail' ? versandEmail : '',
                companyID: selectedProjektpartner?.partnerID,
                contactID: (selectedKontaktperson as any)?.contactID || (selectedKontaktperson as any)?.id,
                bemerkungen,
                status: parseInt(status),
                anzahlMahnungen: parseInt(mahnungen),
                gegenzeichnung1Id: gegenzeichnung1Id ? parseInt(gegenzeichnung1Id) : null,
                gegenzeichnung2Id: gegenzeichnung2Id ? parseInt(gegenzeichnung2Id) : null,
                kontoId: 1,
                benutzerFreigabeAngefordert: benutzerFreigabeAngefordert ? 1 : 0,
            };

            if (isEditMode && editingRechnung?.rechnungID) {
                await dataService.updateRechnung(rechnungData);
            } else {
                await dataService.addRechnung(rechnungData);
            }

            // Refresh list
            const data = await dataService.getRechnungenListe(project.projektID);
            setRechnungen(data || []);
            handleDrawerClose();
        } catch (err) {
            console.error('Error saving invoice:', err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteRechnung = async () => {
        if (!dataService || !editingRechnung?.rechnungID || project?.projektID === undefined) return;

        try {
            setIsSaving(true);
            await dataService.deleteRechnung(editingRechnung.rechnungID);
            // Refresh list
            const data = await dataService.getRechnungenListe(project.projektID);
            setRechnungen(data || []);
            handleDrawerClose();
        } catch (err) {
            console.error('Error deleting invoice:', err);
        } finally {
            setIsSaving(false);
        }
    };

    // Handler for downloading partnership payment files
    const handleDownloadPartnerzahlungFile = async (dokumentGuid: string, filename: string) => {
        try {
            if (!dataService) return;
            const blob = await dataService.getProjektdokumentBinaries(dokumentGuid);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading file:', err);
        }
    };

    // Handler for opening newly uploaded files
    const handleOpenUploadedFile = (file: File) => {
        try {
            const url = window.URL.createObjectURL(file);
            window.open(url, '_blank');
            // Note: We don't revoke the URL immediately as it's needed by the new window
        } catch (err) {
            console.error('Error opening file:', err);
        }
    };

    // Partnerzahlungen handlers
    const handleAddPartnerzahlung = () => {
        setEditingPartnerzahlung(null);
        setIsPartnerzahlungEditMode(false);
        setPartnerzahlungBetrag('');
        setPartnerzahlungIban('');
        setPartnerzahlungBetreff('');
        setPartnerzahlungZahlungsdatum('');
        setPartnerzahlungAnhangName('');
        setPartnerzahlungZahlungsnachweisName('');
        setPartnerzahlungAnhang(null);
        setPartnerzahlungZahlungsnachweis(null);
        setSelectedProjektpartner(null);
        setSelectedKontaktperson(null);
        setIsPartnerzahlungDrawerOpen(true);
    };

    const handleEditPartnerzahlung = async (zahlung: any) => {
        try {
            const fullPartnerzahlung = await dataService?.getRechnung(zahlung.rechnungID);
            console.log("Full Partnerzahlung", fullPartnerzahlung);
            setEditingPartnerzahlung(fullPartnerzahlung);
            setIsPartnerzahlungEditMode(true);

            // Get amount from Teilbetraege array (first element for Partnerzahlung)
            const amount = fullPartnerzahlung?.teilbetraege && fullPartnerzahlung.teilbetraege.length > 0
                ? fullPartnerzahlung.teilbetraege[0].betragNetto?.toString() || ''
                : '';
            setPartnerzahlungBetrag(amount);

            setPartnerzahlungIban(fullPartnerzahlung?.zeitraum || '');
            setPartnerzahlungBetreff(fullPartnerzahlung?.bemerkungen || '');
            setPartnerzahlungZahlungsdatum(fullPartnerzahlung?.reDatum ? fullPartnerzahlung.reDatum.toString().substring(0, 10) : '');
            setPartnerzahlungAnhangName(fullPartnerzahlung?.dateinameAnhang || '');
            setPartnerzahlungZahlungsnachweisName(fullPartnerzahlung?.dateinameNachweis || '');

            // Set the selected partner using companyID
            if (fullPartnerzahlung?.companyID) {
                const partner = projektpartnerList.find((p) => p.partnerID === fullPartnerzahlung.companyID);
                setSelectedProjektpartner(partner || null);
            }

            // Set the selected contact using contactID
            if (fullPartnerzahlung?.contactID) {
                const contact = kontaktpersonList.find((c: any) =>
                    c.contactID === fullPartnerzahlung.contactID || c.id === fullPartnerzahlung.contactID
                );
                if (contact) {
                    setSelectedKontaktperson(contact);
                }
            }

            setIsPartnerzahlungDrawerOpen(true);
        } catch (err) {
            console.error('Error loading partner payment:', err);
        }
    };

    const handleConfirmDeletePartnerzahlung = async () => {
        if (!dataService || !editingPartnerzahlung?.zahlungID || project?.projektID === undefined) return;

        try {
            setIsSavingPartnerzahlung(true);
            await dataService.deletePartnerzahlung(editingPartnerzahlung.zahlungID);
            // Refresh list
            const data = await dataService.getPartnerzahlungenListe(project.projektID);
            setPartnerzahlungen(data || []);
            handlePartnerzahlungDrawerClose();
        } catch (err) {
            console.error('Error deleting partner payment:', err);
        } finally {
            setIsSavingPartnerzahlung(false);
        }
    };

    const handlePartnerzahlungDrawerClose = () => {
        setIsPartnerzahlungDrawerOpen(false);
        setEditingPartnerzahlung(null);
        setIsPartnerzahlungEditMode(false);
        setPartnerzahlungBetrag('');
        setPartnerzahlungIban('');
        setPartnerzahlungBetreff('');
        setPartnerzahlungZahlungsdatum('');
        setPartnerzahlungAnhangName('');
        setPartnerzahlungZahlungsnachweisName('');
        setPartnerzahlungAnhang(null);
        setPartnerzahlungZahlungsnachweis(null);
        setSelectedProjektpartner(null);
        setSelectedKontaktperson(null);
    };

    const handleSavePartnerzahlung = async () => {
        if (!dataService || !project?.projektID) return;

        try {
            setIsSavingPartnerzahlung(true);

            const partnerzahlungData: any = {
                rechnungID: editingPartnerzahlung?.rechnungID || 0,
                partnerzahlungID: editingPartnerzahlung?.partnerzahlungID || 0,
                projektID: project.projektID,
                betrag: parseFloat(partnerzahlungBetrag) || 0,
                betreff: partnerzahlungBetreff,
                zeitraum: partnerzahlungIban,
                companyID: selectedProjektpartner?.partnerID,
                contactID: (selectedKontaktperson as any)?.contactID || (selectedKontaktperson as any)?.id,
                bemerkungen: partnerzahlungBetreff,
                teilbetraege: [
                    {
                        leistung: 'Partnerzahlung',
                        betragNetto: parseFloat(partnerzahlungBetrag) || 0,
                        uStSatz: 0,
                        werbeAbgabe: 0,
                    },
                ],
                reDatum: partnerzahlungZahlungsdatum,
                downloadUrlAnhang: editingPartnerzahlung?.downloadUrlAnhang || '',
                downloadUrlNachweis: editingPartnerzahlung?.downloadUrlNachweis || '',
            };

            // Handle file uploads as base64
            if (partnerzahlungAnhang) {
                const reader = new FileReader();
                await new Promise((resolve, reject) => {
                    reader.onload = () => {
                        const base64Data = reader.result as string;
                        const base64String = base64Data.split(',')[1];
                        partnerzahlungData.binaerDatenAnhang = base64String;
                        partnerzahlungData.dateinameAnhang = partnerzahlungAnhang.name;
                        resolve(null);
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(partnerzahlungAnhang);
                });
            }

            if (partnerzahlungZahlungsnachweis) {
                const reader = new FileReader();
                await new Promise((resolve, reject) => {
                    reader.onload = () => {
                        const base64Data = reader.result as string;
                        const base64String = base64Data.split(',')[1];
                        partnerzahlungData.binaerDatenNachweis = base64String;
                        partnerzahlungData.dateinameNachweis = partnerzahlungZahlungsnachweis.name;
                        resolve(null);
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(partnerzahlungZahlungsnachweis);
                });
            }

            if (isPartnerzahlungEditMode && editingPartnerzahlung?.rechnungID) {
                await dataService.updatePartnerzahlung(partnerzahlungData);
            } else {
                await dataService.addPartnerzahlung(partnerzahlungData);
            }

            // Refresh list
            const data = await dataService.getPartnerzahlungenListe(project.projektID);
            setPartnerzahlungen(data || []);
            handlePartnerzahlungDrawerClose();
        } catch (err) {
            console.error('Error saving partner payment:', err);
        } finally {
            setIsSavingPartnerzahlung(false);
        }
    };

    const togglePartnerCompanyExpand = (companyId: number) => {
        setExpandedPartnerCompanies((prev) => {
            const updated = new Set(prev);
            if (updated.has(companyId)) {
                updated.delete(companyId);
            } else {
                updated.add(companyId);
            }
            return updated;
        });
    };

    if (isLoading) {
        return (
            <div className={styles.container}>
                <TabList defaultSelectedValue="rechnungen">
                    <Tab value="rechnungen">Invoices</Tab>
                    <Tab value="partnerzahlungen">Partner Payments</Tab>
                </TabList>
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading invoices..." />
                </div>
            </div>
        );
    }

    return (
        <>
            {messageBarVisible && (
                <MessageBar intent={messageBarContent.includes('Error') ? 'error' : 'success'} style={{ marginBottom: tokens.spacingVerticalM }}>
                    <MessageBarBody>{messageBarContent}</MessageBarBody>
                </MessageBar>
            )}
            <div className={styles.container}>
                <TabList defaultSelectedValue="rechnungen">
                    <Tab value="rechnungen">Invoices</Tab>
                </TabList>

                <div className={styles.tabContent}>
                    {/* Rechnungen Section */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, marginBottom: tokens.spacingVerticalXL }}>
                        <div className={styles.toolbar}>
                            <Button
                                icon={<AddRegular />}
                                onClick={handleAddClick}
                                appearance="primary"
                                disabled={!canEditInvoices}
                                title={!canEditInvoices ? 'You do not have permission to add invoices' : 'Add new invoice'}
                            >
                                Add Invoice
                            </Button>
                            <ExcelExportButton
                                items={rechnungen}
                                columns={INVOICE_EXCEL_COLUMNS}
                                fileName={`Invoices_${new Date().toISOString().split('T')[0]}`}
                                sheetName="Invoices"
                            />
                        </div>

                        {rechnungen.length === 0 ? (
                            <div className={styles.emptyState}>No invoices found. Click "Add Invoice" to create one.</div>
                        ) : (
                            <div className={styles.tableContainer}>
                                <Table
                                    size="small">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHeaderCell style={{ width: '120px', minWidth: '120px' }}>Actions</TableHeaderCell>
                                            {canApproveRechnungen && <TableHeaderCell style={{ width: '100px', minWidth: '100px' }}>Approve</TableHeaderCell>}
                                            <TableHeaderCell>Invoice Nr</TableHeaderCell>
                                            <TableHeaderCell>Date</TableHeaderCell>
                                            <TableHeaderCell>Contact</TableHeaderCell>
                                            <TableHeaderCell>Status</TableHeaderCell>
                                            <TableHeaderCell>Gross</TableHeaderCell>
                                            <TableHeaderCell>Net</TableHeaderCell>
                                            <TableHeaderCell>Gross Open</TableHeaderCell>
                                            <TableHeaderCell>Net Open</TableHeaderCell>
                                            <TableHeaderCell>Last Payment</TableHeaderCell>
                                            <TableHeaderCell>Complete</TableHeaderCell>
                                            <TableHeaderCell>Reminders</TableHeaderCell>
                                            <TableHeaderCell>Notes</TableHeaderCell>

                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(() => {
                                            const statusMap = RECHNUNG_STATUS_TEXT;

                                            // Group rechnungen by companyId
                                            const groupedByCompany = rechnungen.reduce((acc, rechnung) => {
                                                const companyId = rechnung.companyID;
                                                if (!acc[companyId]) {
                                                    acc[companyId] = {
                                                        companyTitle: rechnung.companyTitle || 'Unknown',
                                                        rechnungen: [],
                                                    };
                                                }
                                                acc[companyId].rechnungen.push(rechnung);
                                                return acc;
                                            }, {} as Record<number, { companyTitle: string; rechnungen: any[] }>);

                                            let grandTotalBrutto = 0;
                                            let grandTotalNetto = 0;
                                            const rows: React.ReactNode[] = [];

                                            // Render each company group
                                            Object.entries(groupedByCompany).forEach(([companyIdStr, groupEntry]) => {
                                                const companyId = parseInt(companyIdStr);
                                                const group = groupEntry as { companyTitle: string; rechnungen: any[] };
                                                let groupTotalBrutto = 0;
                                                let groupTotalNetto = 0;
                                                const isExpanded = expandedCompanies.has(companyId);

                                                // Company header row with collapse/expand button
                                                rows.push(
                                                    <TableRow key={`company-header-${companyId}`} style={{
                                                        backgroundColor: tokens.colorNeutralBackground2,
                                                        fontWeight: 'bold',
                                                        cursor: 'pointer',
                                                    }}>
                                                        <TableCell style={{ padding: 0, width: 'auto' }}>
                                                            <Button
                                                                icon={isExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                                                                onClick={() => toggleCompanyExpand(companyId)}
                                                                appearance="subtle"
                                                                style={{ padding: tokens.spacingHorizontalXS }}
                                                            />
                                                        </TableCell>
                                                        <TableCell colSpan={canApproveRechnungen ? 13 : 12}>
                                                            {group.companyTitle}
                                                        </TableCell>
                                                    </TableRow>
                                                );

                                                // Only render invoice rows if company is expanded
                                                if (isExpanded) {
                                                    // Invoice rows for this company
                                                    group.rechnungen.forEach((rechnung: any) => {
                                                        const invoiceNr = rechnung.title ? rechnung.title.split('/')[0] : '';
                                                        const statusText = statusMap[rechnung.status] || rechnung.status;
                                                        const brutto = rechnung.betragBrutto || 0;
                                                        const netto = rechnung.betragNetto || 0;

                                                        groupTotalBrutto += brutto;
                                                        groupTotalNetto += netto;

                                                        rows.push(
                                                            <TableRow key={rechnung.rechnungID} className={styles.tableRow}>
                                                                <TableCell style={{ width: '120px', minWidth: '120px', display: 'flex', gap: '4px' }}>
                                                                    <Button
                                                                        icon={<EditRegular />}
                                                                        onClick={() => handleEditClick(rechnung)}
                                                                        appearance="subtle"
                                                                        disabled={!canEditInvoices}
                                                                        title={!canEditInvoices ? 'You do not have permission to edit invoices' : 'Edit invoice'}
                                                                    />
                                                                    {canDownloadRechnungen && (<Menu>
                                                                        <MenuTrigger disableButtonEnhancement>
                                                                            <MenuButton
                                                                                appearance="subtle"
                                                                                disabled={!canDownloadRechnungen || isDownloading}
                                                                                title={!canDownloadRechnungen ? 'You do not have permission to download invoices' : 'Download invoice'}
                                                                                icon={<MoreVerticalRegular />}
                                                                            />
                                                                        </MenuTrigger>
                                                                        <MenuPopover>
                                                                            <MenuList>
                                                                                <MenuItem onClick={() => handleDownloadRechnung(rechnung, true)}>
                                                                                    <FileTypeIcon extension="pdf" size={16} />
                                                                                    Download as PDF
                                                                                </MenuItem>
                                                                                <MenuItem onClick={() => handleDownloadRechnung(rechnung, false)}>
                                                                                    <FileTypeIcon extension="docx" size={16} />
                                                                                    Download as DOCX
                                                                                </MenuItem>
                                                                                <MenuItem onClick={() => handleDownloadRechnung(rechnung, true, 'without')}>
                                                                                    <FileTypeIcon extension="pdf" size={16} />
                                                                                    Download as Pdf (NotePaper)
                                                                                </MenuItem>
                                                                                <MenuItem onClick={() => handleImportRechnungToDocuments(rechnung)}>
                                                                                    <SaveRegular />
                                                                                    Save Invoice to ProjectDocuments
                                                                                </MenuItem>
                                                                                <MenuItem onClick={() => handleManageZahlungseingaenge(rechnung)}>
                                                                                    <EditRegular />
                                                                                    Manage Payment Receipts
                                                                                </MenuItem>
                                                                            </MenuList>
                                                                        </MenuPopover>
                                                                    </Menu>)}
                                                                </TableCell>
                                                                {canApproveRechnungen && (
                                                                    <TableCell style={{ width: '100px', minWidth: '100px' }}>
                                                                        {rechnung.status === 1 ? (
                                                                            <Button
                                                                                onClick={() => handleApproveRechnung(rechnung)}
                                                                                size="small"
                                                                                title="Request approval for this invoice"
                                                                            >
                                                                                Approve
                                                                            </Button>
                                                                        ) : (
                                                                            <span style={{ color: tokens.colorStatusSuccessForeground1, fontWeight: '500' }}>
                                                                                Approved
                                                                            </span>
                                                                        )}
                                                                    </TableCell>
                                                                )}
                                                                <TableCell>{invoiceNr}</TableCell>
                                                                <TableCell>{rechnung.reDatumAsText}</TableCell>
                                                                <TableCell>{rechnung.contactName}</TableCell>
                                                                <TableCell>{statusText}</TableCell>
                                                                <TableCell>{formatEuropeanCurrency(brutto)}</TableCell>
                                                                <TableCell>{formatEuropeanCurrency(netto)}</TableCell>
                                                                <TableCell>{formatEuropeanCurrency(rechnung.betragBruttoOffen)}</TableCell>
                                                                <TableCell>{formatEuropeanCurrency(rechnung.betragNettoOffen)}</TableCell>
                                                                <TableCell>{rechnung.datumLetzteZahlungAsText || '-'}</TableCell>
                                                                <TableCell>No</TableCell>
                                                                <TableCell>{rechnung.anzahlMahnungen || 0}</TableCell>
                                                                <TableCell>{rechnung.bemerkungenAsShortText || '-'}</TableCell>
                                                            </TableRow>
                                                        );
                                                    });

                                                    // Company subtotal row (only show if expanded)
                                                    rows.push(
                                                        <TableRow key={`company-total-${companyId}`} style={{
                                                            backgroundColor: tokens.colorNeutralBackground1Hover,
                                                            fontWeight: 'bold',
                                                        }}>
                                                            <TableCell colSpan={canApproveRechnungen ? 6 : 5}>Subtotal {group.companyTitle}</TableCell>
                                                            <TableCell>{formatEuropeanCurrency(groupTotalBrutto)}</TableCell>
                                                            <TableCell>{formatEuropeanCurrency(groupTotalNetto)}</TableCell>
                                                            <TableCell colSpan={6}></TableCell>
                                                        </TableRow>
                                                    );
                                                }

                                                // Always add to grand total, even if collapsed
                                                group.rechnungen.forEach((rechnung: any) => {
                                                    const brutto = rechnung.betragBrutto || 0;
                                                    const netto = rechnung.betragNetto || 0;
                                                    grandTotalBrutto += brutto;
                                                    grandTotalNetto += netto;
                                                });
                                            });

                                            // Grand total row
                                            rows.push(
                                                <TableRow key="grand-total" style={{
                                                    backgroundColor: tokens.colorNeutralBackground2,
                                                    fontWeight: 'bold',
                                                }}>
                                                    <TableCell colSpan={canApproveRechnungen ? 6 : 5}>Grand Total</TableCell>
                                                    <TableCell>{formatEuropeanCurrency(grandTotalBrutto)}</TableCell>
                                                    <TableCell>{formatEuropeanCurrency(grandTotalNetto)}</TableCell>
                                                    <TableCell colSpan={canApproveRechnungen ? 7 : 6}></TableCell>
                                                </TableRow>
                                            );

                                            return rows;
                                        })()}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>

                    {/* Partnerzahlungen Section */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
                        <div className={styles.toolbar}>
                            <Button
                                icon={<AddRegular />}
                                onClick={handleAddPartnerzahlung}
                                appearance="primary"
                                disabled={!canEditInvoices}
                                title={!canEditInvoices ? 'You do not have permission to add partner payments' : 'Add new partner payment'}
                            >
                                Add Partner Payment
                            </Button>
                            <ExcelExportButton
                                items={partnerzahlungen}
                                columns={PARTNER_PAYMENT_EXCEL_COLUMNS}
                                fileName={`PartnerPayments_${new Date().toISOString().split('T')[0]}`}
                                sheetName="Partner Payments"
                            />
                        </div>

                        {partnerzahlungen.length === 0 ? (
                            <div className={styles.emptyState}>No partner payments found. Click "Add Partner Payment" to create one.</div>
                        ) : (
                            <div className={styles.tableContainer}>
                                <Table
                                    size="small"
                                >
                                    <TableHeader>
                                        <TableRow>
                                            <TableHeaderCell style={{ width: '70px', minWidth: '70px' }}>Actions</TableHeaderCell>
                                            <TableHeaderCell>Notes</TableHeaderCell>
                                            <TableHeaderCell>Amount</TableHeaderCell>
                                            <TableHeaderCell>Amount Open</TableHeaderCell>
                                            <TableHeaderCell>Payment Date</TableHeaderCell>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(() => {
                                            // Group partnerzahlungen by companyId
                                            const groupedByCompanyPZ = partnerzahlungen.reduce((acc, zahlung) => {
                                                const companyId = zahlung.companyID;
                                                if (!acc[companyId]) {
                                                    acc[companyId] = {
                                                        companyTitle: zahlung.companyTitle || 'Unknown',
                                                        zahlungen: [],
                                                    };
                                                }
                                                acc[companyId].zahlungen.push(zahlung);
                                                return acc;
                                            }, {} as Record<number, { companyTitle: string; zahlungen: any[] }>);

                                            let grandTotalPartner = 0;
                                            let grandTotalPartnerOffen = 0;
                                            const rowsPZ: React.ReactNode[] = [];

                                            // Render each company group
                                            Object.entries(groupedByCompanyPZ).forEach(([companyIdStr, groupEntry]) => {
                                                const companyId = parseInt(companyIdStr);
                                                const group = groupEntry as { companyTitle: string; zahlungen: any[] };
                                                let groupTotalPartner = 0;
                                                let groupTotalPartnerOffen = 0;
                                                const isExpanded = expandedPartnerCompanies.has(companyId);

                                                // Company header row with collapse/expand button
                                                rowsPZ.push(
                                                    <TableRow key={`partner-company-header-${companyId}`} style={{
                                                        backgroundColor: tokens.colorNeutralBackground2,
                                                        fontWeight: 'bold',
                                                        cursor: 'pointer',
                                                    }}>
                                                        <TableCell style={{ padding: 0, width: 'auto' }}>
                                                            <Button
                                                                icon={isExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                                                                onClick={() => togglePartnerCompanyExpand(companyId)}
                                                                appearance="subtle"
                                                                style={{ padding: tokens.spacingHorizontalXS }}
                                                            />
                                                        </TableCell>
                                                        <TableCell colSpan={4}>
                                                            {group.companyTitle}
                                                        </TableCell>
                                                    </TableRow>
                                                );

                                                // Only render payment rows if company is expanded
                                                if (isExpanded) {
                                                    // Payment rows for this company
                                                    group.zahlungen.forEach((zahlung: any) => {
                                                        const betragNetto = zahlung.betragNetto || 0;
                                                        const betragNettoOffen = zahlung.zahlungen[0].zeDatum === undefined ? zahlung.betragNetto : 0;
                                                        groupTotalPartner += betragNetto;
                                                        groupTotalPartnerOffen += betragNettoOffen;
                                                        const paymentDate = zahlung.zahlungen && zahlung.zahlungen.length > 0
                                                            ? zahlung.zahlungen[0].zeDatumAsText
                                                            : '-';

                                                        rowsPZ.push(
                                                            <TableRow key={zahlung.rechnungID} className={styles.tableRow}>
                                                                <TableCell style={{ width: '70px', minWidth: '70px' }}>
                                                                    <Button
                                                                        icon={<EditRegular />}
                                                                        onClick={() => handleEditPartnerzahlung(zahlung)}
                                                                        appearance="subtle"
                                                                        disabled={!canEditInvoices}
                                                                        title={!canEditInvoices ? 'You do not have permission to edit partner payments' : 'Edit partner payment'}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{zahlung.bemerkungen || '-'}</TableCell>
                                                                <TableCell>{formatEuropeanCurrency(betragNetto)}</TableCell>
                                                                <TableCell>{formatEuropeanCurrency(betragNettoOffen)}</TableCell>
                                                                <TableCell>{paymentDate}</TableCell>
                                                            </TableRow>
                                                        );
                                                    });

                                                    // Company subtotal row (only show if expanded)
                                                    rowsPZ.push(
                                                        <TableRow key={`partner-company-total-${companyId}`} style={{
                                                            backgroundColor: tokens.colorNeutralBackground1Hover,
                                                            fontWeight: 'bold',
                                                        }}>
                                                            <TableCell colSpan={2}>Subtotal {group.companyTitle}</TableCell>
                                                            <TableCell>{formatEuropeanCurrency(groupTotalPartner)}</TableCell>
                                                            <TableCell>{formatEuropeanCurrency(groupTotalPartnerOffen)}</TableCell>
                                                            <TableCell></TableCell>
                                                        </TableRow>
                                                    );
                                                }

                                                // Always add to grand total, even if collapsed
                                                group.zahlungen.forEach((zahlung: any) => {
                                                    const betragNetto = zahlung.betragNetto || 0;
                                                    const betragNettoOffen = zahlung.betragNettoOffen || 0;
                                                    grandTotalPartner += betragNetto;
                                                    grandTotalPartnerOffen += betragNettoOffen;
                                                });
                                            });

                                            // Grand total row
                                            rowsPZ.push(
                                                <TableRow key="partner-grand-total" style={{
                                                    backgroundColor: tokens.colorNeutralBackground2,
                                                    fontWeight: 'bold',
                                                }}>
                                                    <TableCell colSpan={2}>Grand Total</TableCell>
                                                    <TableCell>{formatEuropeanCurrency(grandTotalPartner)}</TableCell>
                                                    <TableCell>{formatEuropeanCurrency(grandTotalPartnerOffen)}</TableCell>
                                                    <TableCell></TableCell>
                                                </TableRow>
                                            );

                                            return rowsPZ;
                                        })()}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Drawer for Add/Edit */}
                <Drawer
                    open={isDrawerOpen}
                    onOpenChange={(_, { open }) => !open && handleDrawerClose()}
                    position="end"
                    size="large"
                >
                    <DrawerHeader>
                        <DrawerHeaderTitle>{isEditMode ? 'Edit Invoice' : 'Add Invoice'}</DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody>
                        {validationErrors.length > 0 && (
                            <div style={{
                                backgroundColor: '#fed7d7',
                                border: '1px solid #fc8181',
                                borderRadius: '4px',
                                padding: '12px',
                                marginBottom: '16px'
                            }}>
                                <div style={{ color: '#c53030', fontWeight: 'bold', marginBottom: '8px' }}>Validation Errors:</div>
                                {validationErrors.map((error, idx) => (
                                    <div key={idx} style={{ color: '#c53030', marginBottom: idx < validationErrors.length - 1 ? '4px' : '0' }}>• {error}</div>
                                ))}
                            </div>
                        )}
                        <div className={styles.drawerForm}>
                            {/* Section 1: Rechnungsempfänger */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionTitle}>Invoice Recipient</div>

                                <div className={styles.formGroup}>
                                    <Label required className={styles.formLabel}>Project Partner</Label>
                                    <Combobox
                                        value={selectedProjektpartner?.bezeichnung1 || ''}
                                        onOptionSelect={(_, option) => {
                                            const selected = projektpartnerList.find(
                                                (p) => p.bezeichnung1 === option.optionValue
                                            );
                                            setSelectedProjektpartner(selected);
                                        }}
                                    >
                                        {projektpartnerList.map((partner) => {
                                            const partnerName = partner.bezeichnung1 || `Partner ${partner.partnerID}`;
                                            return (
                                                <Option key={partner.partnerID} value={partnerName}>
                                                    {partnerName}
                                                </Option>
                                            );
                                        })}
                                    </Combobox>
                                </div>

                                <div className={styles.formGroup}>
                                    <Label required className={styles.formLabel}>Contact Person</Label>
                                    <Combobox
                                        value={(selectedKontaktperson as any)?.contactTitle || (selectedKontaktperson as any)?.name || ''}
                                        onOptionSelect={(_, option) => {
                                            const selected = kontaktpersonList.find(
                                                (c: any) => (c.contactTitle || c.name) === option.optionValue
                                            );
                                            setSelectedKontaktperson(selected);
                                        }}
                                        disabled={!selectedProjektpartner}
                                    >
                                        {kontaktpersonList.map((contact: any) => {
                                            const contactName = contact.contactTitle || contact.name || `Contact ${contact.id}`;
                                            return (
                                                <Option key={contact.id} value={contactName}>
                                                    {contactName}
                                                </Option>
                                            );
                                        })}
                                    </Combobox>
                                </div>

                                <div className={styles.formGroup}>
                                    <Label required htmlFor={versandartSelectId} className={styles.formLabel}>Shipping Method</Label>
                                    <Select
                                        id={versandartSelectId}
                                        value={versandart}
                                        onChange={(_, data) => setVersandart(data.value || 'Post')}
                                    >
                                        <option value="Post">Post</option>
                                        <option value="E-Mail">E-Mail</option>
                                    </Select>
                                </div>

                                {versandart === 'E-Mail' && (
                                    <div className={styles.formGroup}>
                                        <Label required className={styles.formLabel}>Email Address</Label>
                                        <Input
                                            type="email"
                                            value={versandEmail}
                                            onChange={(_, data) => setVersandEmail(data.value || '')}
                                            placeholder="Enter recipient email address"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Section 2: Rechnungsdaten */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionTitle}>Invoice Data</div>

                                <div className={styles.formGroup}>
                                    <Label className={styles.formLabel}>Invoice Number</Label>
                                    <Input
                                        value={rechnungsNr}
                                        onChange={(_, data) => setRechnungsNr(data.value)}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <Label className={styles.formLabel}>Invoice Date</Label>
                                    <Input
                                        type="date"
                                        value={rechnungsDatum}
                                        onChange={(_, data) => setRechnungsDatum(data.value)}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <Label required htmlFor={rechnungsspracheSelectId} className={styles.formLabel}>Invoice Language</Label>
                                    <Select
                                        id={rechnungsspracheSelectId}
                                        value={rechnungssprache}
                                        onChange={(_, data) => setRechnungssprache(data.value || '0')}
                                    >
                                        <option value="0">German</option>
                                        <option value="1">English</option>
                                    </Select>
                                </div>

                                {/* Zahlungstermine Selection */}
                                <div className={styles.formGroup}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalS }}>
                                        <Label className={styles.formLabel}>Payment Schedule</Label>
                                        <Button
                                            size="small"
                                            appearance="subtle"
                                            onClick={() => {
                                                if (selectedZahlungstermineIds.size === zahlungsplan.length && zahlungsplan.length > 0) {
                                                    setSelectedZahlungstermineIds(new Set());
                                                } else {
                                                    const allIds = new Set(zahlungsplan.map((z: any) => z.id || z.zahlungsplanId));
                                                    setSelectedZahlungstermineIds(allIds);
                                                }
                                            }}
                                        >
                                            {selectedZahlungstermineIds.size === zahlungsplan.length && zahlungsplan.length > 0 ? 'Deselect All' : 'Select All'}
                                        </Button>
                                    </div>
                                    {zahlungsplan.length === 0 ? (
                                        <div style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 }}>
                                            No payment schedules available.
                                        </div>
                                    ) : (
                                        <div className={styles.checkboxList}>
                                            {zahlungsplan.map((item: any) => {
                                                const itemId = item.id || item.zahlungsplanId;
                                                const isSelected = selectedZahlungstermineIds.has(itemId);
                                                return (
                                                    <div key={itemId} className={styles.checkboxItem}>
                                                        <input
                                                            type="checkbox"
                                                            id={`zahlungstermin-${itemId}`}
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                const newSet = new Set(selectedZahlungstermineIds);
                                                                if (e.target.checked) {
                                                                    newSet.add(itemId);
                                                                } else {
                                                                    newSet.delete(itemId);
                                                                }
                                                                setSelectedZahlungstermineIds(newSet);
                                                            }}
                                                        />
                                                        <label htmlFor={`zahlungstermin-${itemId}`} style={{ cursor: 'pointer', fontSize: tokens.fontSizeBase300 }}>
                                                            {item.reDatumAsText || item.zahlungsDatum || `Payment ${itemId}`}
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <Label required className={styles.formLabel}>Performance Period</Label>
                                    <Input
                                        value={leistungszeitraum}
                                        onChange={(_, data) => setLeistungszeitraum(data.value)}
                                        placeholder="e.g., Jan 2026 - Mar 2026"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <Label required htmlFor={kontoSelectId} className={styles.formLabel}>Account</Label>
                                    <Select
                                        id={kontoSelectId}
                                        value={kontoId}
                                        onChange={(_, data) => setKontoId(data.value || '1')}
                                    >
                                        {kontoList.length > 0 ? (
                                            kontoList.map((konto) => (
                                                <option key={konto.kontoId} value={konto.kontoId.toString()}>
                                                    {konto.kontoBankName} (BLZ: {konto.kontoBlz} / KtoNr: {konto.kontoNummer})
                                                </option>
                                            ))
                                        ) : (
                                            <option value="1">-- No Account Available --</option>
                                        )}
                                    </Select>
                                </div>

                                <div className={styles.formGroup}>
                                    <Label required htmlFor={zahlungsfristSelectId} className={styles.formLabel}>Payment Terms</Label>
                                    <Select
                                        id={zahlungsfristSelectId}
                                        value={zahlungsfristId}
                                        onChange={(_, data) => setZahlungsfristId(data.value || '1')}
                                    >
                                        <option value="1">Within the next 14 days</option>
                                        <option value="2">Within the next 30 days</option>
                                        <option value="3">Immediately upon receipt of invoice</option>
                                    </Select>
                                </div>

                                <div className={styles.formGroup}>
                                    <Label required htmlFor={gegenzeichnung1SelectId} className={styles.formLabel}>Countersignature 1</Label>
                                    <Select
                                        id={gegenzeichnung1SelectId}
                                        value={gegenzeichnung1Id}
                                        onChange={(_, data) => setGegenzeichnung1Id(data.value || '')}
                                    >
                                        <option value="">-- Select --</option>
                                        {countersignatures.map((sig) => (
                                            <option key={sig.id} value={sig.id.toString()}>
                                                {sig.name}
                                            </option>
                                        ))}
                                    </Select>
                                </div>

                                <div className={styles.formGroup}>
                                    <Label htmlFor={gegenzeichnung2SelectId} className={styles.formLabel}>Countersignature 2</Label>
                                    <Select
                                        id={gegenzeichnung2SelectId}
                                        value={gegenzeichnung2Id}
                                        onChange={(_, data) => setGegenzeichnung2Id(data.value || '')}
                                    >
                                        <option value="">-- Select --</option>
                                        {countersignatures.map((sig) => (
                                            <option key={sig.id} value={sig.id.toString()}>
                                                {sig.name}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </div>

                            {/* Section 3: Teilbeträge */}
                            <div className={styles.formSection}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalS }}>
                                    <div className={styles.sectionTitle}>Partial Amounts</div>
                                    <Button
                                        icon={<AddRegular />}
                                        onClick={handleAddTeilbetrag}
                                        appearance="subtle"
                                        size="small"
                                        disabled={!editingRechnung}
                                        title={!editingRechnung ? 'Save the invoice first before adding partial amounts' : 'Add partial amount'}
                                    >
                                        Add
                                    </Button>
                                </div>
                                {teilbeträge.length === 0 ? (
                                    <div style={{ color: tokens.colorNeutralForeground3 }}>
                                        No partial amounts added yet.
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHeaderCell style={{ width: '70px', minWidth: '70px' }}>Actions</TableHeaderCell>
                                                <TableHeaderCell>Service</TableHeaderCell>
                                                <TableHeaderCell>Net Amount</TableHeaderCell>
                                                <TableHeaderCell>Gross Amount</TableHeaderCell>
                                                <TableHeaderCell>Tax Rate</TableHeaderCell>
                                                <TableHeaderCell>Advertising Fee</TableHeaderCell>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {teilbeträge.map((betrag) => (
                                                <TableRow key={betrag.itemId}>
                                                    <TableCell style={{ width: '70px', minWidth: '70px' }}>
                                                        <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                                                            <Button
                                                                icon={<EditRegular />}
                                                                onClick={() => handleEditTeilbetrag(betrag)}
                                                                appearance="subtle"
                                                                size="small"
                                                                title="Edit"
                                                            />
                                                            <Button
                                                                icon={<DeleteRegular />}
                                                                onClick={() => handleDeleteTeilbetrag(betrag)}
                                                                appearance="subtle"
                                                                size="small"
                                                                title="Delete"
                                                            />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>{betrag.leistung}</TableCell>
                                                    <TableCell>{formatEuropeanCurrency(betrag.betragNetto)}</TableCell>
                                                    <TableCell>{formatEuropeanCurrency(betrag.betragBrutto)}</TableCell>
                                                    <TableCell>{betrag.uStSatz}%</TableCell>
                                                    <TableCell>{betrag.werbeAbgabe && betrag.werbeAbgabe !== 0 ? '5%' : 'No'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </div>

                            {/* Section 4: Zusatzinformation */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionTitle}>Additional Information</div>

                                <div className={styles.formGroup}>
                                    <Label className={styles.formLabel}>Status</Label>
                                    <Select
                                        value={status}
                                        onChange={(_, data) => setStatus(data.value)}
                                        disabled={selectedRole !== 'Administratoren'}
                                    >
                                        <option value="1">Draft</option>
                                        <option value="2">Approved</option>
                                        <option value="3">Sent</option>
                                        <option value="4">Completed</option>
                                        <option value="5">Cancelled</option>
                                        <option value="6">Project Manager Approved</option>
                                    </Select>
                                </div>

                                <div className={styles.formGroup}>
                                    <Label required className={styles.formLabel}>Reminders</Label>
                                    <Input
                                        type="number"
                                        value={mahnungen}
                                        onChange={(_, data) => setMahnungen(data.value)}
                                        min="0"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <Label className={styles.formLabel}>Notes</Label>
                                    <Textarea
                                        value={bemerkungen}
                                        onChange={(_, data) => setBemerkungen(data.value)}
                                        rows={3}
                                    />
                                </div>

                                {teilbeträge.length > 0 && (status === '' || editingRechnung?.status === 1) && (
                                    <div className={styles.formGroup}>
                                        <Switch
                                            checked={benutzerFreigabeAngefordert}
                                            onChange={(_, data) => setBenutzerFreigabeAngefordert(data.checked)}
                                            label="Request Approval"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </DrawerBody>

                    <div className={styles.drawerFooter}>
                        <Button
                            onClick={handleSave}
                            appearance="primary"
                            disabled={isSaving || !isFormValid}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button onClick={handleDrawerClose} appearance="secondary">
                            Cancel
                        </Button>
                        <div style={{ flex: 1 }}></div>
                        {isEditMode && (
                            <Button
                                icon={<DeleteRegular />}
                                appearance="outline"
                                disabled={isSaving}
                                onClick={handleDeleteRechnung}
                                style={{ color: 'red' }}
                            >
                                Remove
                            </Button>
                        )}
                    </div>
                </Drawer>

                {/* Teilbetrag Edit Drawer */}
                <Drawer
                    open={isTeilbetragDrawerOpen}
                    onOpenChange={(_, { open }) => !open && handleTeilbetragDrawerClose()}
                    position="end"
                    size="medium"
                >
                    <DrawerHeader>
                        <DrawerHeaderTitle>{isTeilbetragEditMode ? 'Edit Partial Amount' : 'Add Partial Amount'}</DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody>
                        <div className={styles.drawerForm}>
                            <div className={styles.formGroup}>
                                <Label className={styles.formLabel}>Service</Label>
                                <Input
                                    value={teilbetragLeistung}
                                    onChange={(_, data) => setTeilbetragLeistung(data.value)}
                                    placeholder="Enter service description"
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <Label className={styles.formLabel}>Net Amount</Label>
                                <Input
                                    type="number"
                                    value={teilbetragBetragNetto}
                                    onChange={(_, data) => setTeilbetragBetragNetto(data.value)}
                                    placeholder="0.00"
                                    step="0.01"
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <Label htmlFor={teilbetragUStSatzSelectId} className={styles.formLabel}>Tax Rate</Label>
                                <Select
                                    id={teilbetragUStSatzSelectId}
                                    value={teilbetragUStSatz}
                                    onChange={(_, data) => setTeilbetragUStSatz(data.value || '20')}
                                >
                                    <option value="20">20%</option>
                                    <option value="10">10%</option>
                                    <option value="0">0%</option>
                                </Select>
                            </div>

                            <div className={styles.formGroup}>
                                <Label className={styles.formLabel}>Advertising Fee</Label>
                                <Switch
                                    checked={teilbetragWerbeAbgabe}
                                    onChange={(_, data) => setTeilbetragWerbeAbgabe(data.checked)}
                                    label={teilbetragWerbeAbgabe ? 'Yes' : 'No'}
                                />
                            </div>
                        </div>
                    </DrawerBody>

                    <div className={styles.drawerFooter}>
                        <Button
                            onClick={handleSaveTeilbetrag}
                            appearance="primary"
                            disabled={!teilbetragLeistung || !teilbetragBetragNetto}
                        >
                            {isTeilbetragEditMode ? 'Save Changes' : 'Add Partial Amount'}
                        </Button>
                        <Button onClick={handleTeilbetragDrawerClose} appearance="secondary">
                            Cancel
                        </Button>
                    </div>
                </Drawer>

                {/* Partnerzahlung Edit Drawer */}
                <Drawer
                    open={isPartnerzahlungDrawerOpen}
                    onOpenChange={(_, { open }) => !open && handlePartnerzahlungDrawerClose()}
                    position="end"
                    size="large"
                >
                    <DrawerHeader>
                        <DrawerHeaderTitle>{isPartnerzahlungEditMode ? 'Edit Partner Payment' : 'Add Partner Payment'}</DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody>
                        <div className={styles.drawerForm}>
                            {/* Section 1: Partner and Contact */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionTitle}>Partner Information</div>

                                <div className={styles.formGroup}>
                                    <Label className={styles.formLabel}>Project Partner</Label>
                                    <Combobox
                                        value={selectedProjektpartner?.bezeichnung1 || ''}
                                        onOptionSelect={(_, option) => {
                                            const selected = projektpartnerList.find(
                                                (p) => p.bezeichnung1 === option.optionValue
                                            );
                                            setSelectedProjektpartner(selected);
                                        }}
                                    >
                                        {projektpartnerList.map((partner) => {
                                            const partnerName = partner.bezeichnung1 || `Partner ${partner.partnerID}`;
                                            return (
                                                <Option key={partner.partnerID} value={partnerName}>
                                                    {partnerName}
                                                </Option>
                                            );
                                        })}
                                    </Combobox>
                                </div>

                                <div className={styles.formGroup}>
                                    <Label className={styles.formLabel}>Contact Person</Label>
                                    <Combobox
                                        value={(selectedKontaktperson as any)?.contactTitle || (selectedKontaktperson as any)?.name || ''}
                                        onOptionSelect={(_, option) => {
                                            const selected = kontaktpersonList.find(
                                                (c: any) => (c.contactTitle || c.name) === option.optionValue
                                            );
                                            setSelectedKontaktperson(selected);
                                        }}
                                        disabled={!selectedProjektpartner}
                                    >
                                        {kontaktpersonList.map((contact: any) => {
                                            const contactName = contact.contactTitle || contact.name || `Contact ${contact.id}`;
                                            return (
                                                <Option key={contact.id} value={contactName}>
                                                    {contactName}
                                                </Option>
                                            );
                                        })}
                                    </Combobox>
                                </div>
                            </div>

                            {/* Section 2: Payment Details */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionTitle}>Payment Details</div>

                                <div className={styles.formGroup}>
                                    <Label className={styles.formLabel}>Amount</Label>
                                    <Input
                                        type="number"
                                        value={partnerzahlungBetrag}
                                        onChange={(_, data) => setPartnerzahlungBetrag(data.value)}
                                        placeholder="0.00"
                                        step="0.01"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <Label className={styles.formLabel}>IBAN/BIC</Label>
                                    <Input
                                        value={partnerzahlungIban}
                                        onChange={(_, data) => setPartnerzahlungIban(data.value)}
                                        placeholder="Enter IBAN or BIC"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <Label className={styles.formLabel}>Subject</Label>
                                    <Textarea
                                        value={partnerzahlungBetreff}
                                        onChange={(_, data) => setPartnerzahlungBetreff(data.value)}
                                        placeholder="Enter subject/description"
                                        rows={3}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <Label className={styles.formLabel}>Payment Date</Label>
                                    <Input
                                        type="date"
                                        value={partnerzahlungZahlungsdatum}
                                        onChange={(_, data) => setPartnerzahlungZahlungsdatum(data.value)}
                                    />
                                </div>
                            </div>

                            {/* Section 3: Attachments */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionTitle}>Attachments</div>

                                <div className={styles.formGroup}>
                                    <Label className={styles.formLabel}>Attachment (Anhang)</Label>
                                    {partnerzahlungAnhang ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, padding: tokens.spacingVerticalM, backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium }}>
                                            <span style={{ fontSize: tokens.fontSizeBase300, flex: 1 }}>{partnerzahlungAnhangName}</span>
                                            <Button
                                                icon={<ArrowDownloadRegular />}
                                                appearance="subtle"
                                                size="small"
                                                onClick={() => handleOpenUploadedFile(partnerzahlungAnhang)}
                                                title="Open File"
                                            />
                                            <Button
                                                icon={<DeleteRegular />}
                                                appearance="subtle"
                                                size="small"
                                                onClick={() => {
                                                    setPartnerzahlungAnhang(null);
                                                    setPartnerzahlungAnhangName('');
                                                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                                    editingPartnerzahlung && setEditingPartnerzahlung({ ...editingPartnerzahlung, downloadUrlAnhang: '' });
                                                }}
                                                title="Remove File"
                                            />
                                        </div>
                                    ) : partnerzahlungAnhangName && isPartnerzahlungEditMode && editingPartnerzahlung?.downloadUrlAnhang ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, padding: tokens.spacingVerticalM, backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium }}>
                                            <span style={{ fontSize: tokens.fontSizeBase300, flex: 1 }}>{partnerzahlungAnhangName}</span>
                                            <Button
                                                icon={<ArrowDownloadRegular />}
                                                appearance="subtle"
                                                size="small"
                                                onClick={() => handleDownloadPartnerzahlungFile(editingPartnerzahlung.downloadUrlAnhang, partnerzahlungAnhangName)}
                                                title="Download"
                                            />
                                            <Button
                                                icon={<DeleteRegular />}
                                                appearance="subtle"
                                                size="small"
                                                onClick={() => {
                                                    setPartnerzahlungAnhang(null);
                                                    setPartnerzahlungAnhangName('');
                                                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                                    editingPartnerzahlung && setEditingPartnerzahlung({ ...editingPartnerzahlung, downloadUrlAnhang: '' });
                                                }}
                                                title="Delete and Upload New"
                                            />
                                        </div>
                                    ) : (
                                        <input
                                            type="file"
                                            onChange={(evt) => {
                                                const file = (evt.target as HTMLInputElement).files?.[0];
                                                if (file) {
                                                    setPartnerzahlungAnhang(file);
                                                    setPartnerzahlungAnhangName(file.name);
                                                }
                                            }}
                                            style={{ padding: tokens.spacingVerticalM, border: `1px dashed ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium }}
                                        />
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <Label className={styles.formLabel}>Payment Proof (Zahlungsnachweis)</Label>
                                    {partnerzahlungZahlungsnachweis ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, padding: tokens.spacingVerticalM, backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium }}>
                                            <span style={{ fontSize: tokens.fontSizeBase300, flex: 1 }}>{partnerzahlungZahlungsnachweisName}</span>
                                            <Button
                                                icon={<ArrowDownloadRegular />}
                                                appearance="subtle"
                                                size="small"
                                                onClick={() => handleOpenUploadedFile(partnerzahlungZahlungsnachweis)}
                                                title="Open File"
                                            />
                                            <Button
                                                icon={<DeleteRegular />}
                                                appearance="subtle"
                                                size="small"
                                                onClick={() => {
                                                    setPartnerzahlungZahlungsnachweis(null);
                                                    setPartnerzahlungZahlungsnachweisName('');
                                                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                                    editingPartnerzahlung && setEditingPartnerzahlung({ ...editingPartnerzahlung, downloadUrlNachweis: '' });
                                                }}
                                                title="Remove File"
                                            />
                                        </div>
                                    ) : partnerzahlungZahlungsnachweisName && isPartnerzahlungEditMode && editingPartnerzahlung?.downloadUrlNachweis ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, padding: tokens.spacingVerticalM, backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium }}>
                                            <span style={{ fontSize: tokens.fontSizeBase300, flex: 1 }}>{partnerzahlungZahlungsnachweisName}</span>
                                            <Button
                                                icon={<ArrowDownloadRegular />}
                                                appearance="subtle"
                                                size="small"
                                                onClick={() => handleDownloadPartnerzahlungFile(editingPartnerzahlung.downloadUrlNachweis, partnerzahlungZahlungsnachweisName)}
                                                title="Download"
                                            />
                                            <Button
                                                icon={<DeleteRegular />}
                                                appearance="subtle"
                                                size="small"
                                                onClick={() => {
                                                    setPartnerzahlungZahlungsnachweis(null);
                                                    setPartnerzahlungZahlungsnachweisName('');
                                                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                                    editingPartnerzahlung && setEditingPartnerzahlung({ ...editingPartnerzahlung, downloadUrlNachweis: '' });
                                                }}
                                                title="Delete and Upload New"
                                            />
                                        </div>
                                    ) : (
                                        <input
                                            type="file"
                                            onChange={(evt) => {
                                                const file = (evt.target as HTMLInputElement).files?.[0];
                                                if (file) {
                                                    setPartnerzahlungZahlungsnachweis(file);
                                                    setPartnerzahlungZahlungsnachweisName(file.name);
                                                }
                                            }}
                                            style={{ padding: tokens.spacingVerticalM, border: `1px dashed ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, width: '100%' }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </DrawerBody>

                    <div className={styles.drawerFooter}>
                        {isPartnerzahlungEditMode && (
                            <Button
                                onClick={handleConfirmDeletePartnerzahlung}
                                icon={<DeleteRegular />}
                                appearance="outline"
                                style={{ color: 'red' }}
                            >
                                Remove
                            </Button>
                        )}
                        <div style={{ flex: 1 }}></div>
                        <Button
                            onClick={handleSavePartnerzahlung}
                            appearance="primary"
                            disabled={isSavingPartnerzahlung}
                        >
                            {isSavingPartnerzahlung ? 'Saving...' : isPartnerzahlungEditMode ? 'Save Changes' : 'Add Partner Payment'}
                        </Button>
                        <Button onClick={handlePartnerzahlungDrawerClose} appearance="secondary">
                            Cancel
                        </Button>
                    </div>
                </Drawer>
                <Dialog
                    open={deleteConfirmationOpen}
                    onOpenChange={(_event, data) => {
                        if (!data.open) setDeleteConfirmationOpen(false);
                    }}
                >
                    <DialogSurface>
                        <DialogContent>
                            <DialogTitle>Delete Partial Amount</DialogTitle>
                            <DialogBody>
                                <div style={{ marginBottom: tokens.spacingVerticalM }}>
                                    Are you sure you want to delete this partial amount?
                                </div>
                                <div style={{ marginBottom: tokens.spacingVerticalM, color: tokens.colorStatusWarningForeground1 }}>
                                    This action cannot be undone.
                                </div>
                            </DialogBody>
                            <div style={{
                                display: 'flex',
                                gap: tokens.spacingHorizontalM,
                                justifyContent: 'flex-end',
                                padding: tokens.spacingVerticalL,
                                borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
                            }}>
                                <Button
                                    appearance="secondary"
                                    onClick={() => setDeleteConfirmationOpen(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    appearance="primary"
                                    onClick={handleConfirmDelete}
                                >
                                    Delete
                                </Button>
                            </div>
                        </DialogContent>
                    </DialogSurface>
                </Dialog>
                <Dialog
                    open={approvalConfirmationOpen}
                    onOpenChange={(_event, data) => {
                        if (!data.open) setApprovalConfirmationOpen(false);
                    }}
                >
                    <DialogSurface>
                        <DialogContent>
                            <DialogTitle>Approve Invoice</DialogTitle>
                            <DialogBody>
                                <div style={{ marginBottom: tokens.spacingVerticalM }}>
                                    Do you want to approve this invoice?
                                </div>
                            </DialogBody>
                            <div style={{
                                display: 'flex',
                                gap: tokens.spacingHorizontalM,
                                justifyContent: 'flex-end',
                                padding: tokens.spacingVerticalL,
                                borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
                            }}>
                                <Button
                                    appearance="secondary"
                                    onClick={() => setApprovalConfirmationOpen(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    appearance="primary"
                                    onClick={handleConfirmApprove}
                                >
                                    Approve
                                </Button>
                            </div>
                        </DialogContent>
                    </DialogSurface>
                </Dialog>

                {/* Delete Payment Receipt Confirmation Dialog */}
                <DeleteConfirmationDialog
                    open={deleteZahlungseingangConfirmationOpen}
                    onOpenChange={setDeleteZahlungseingangConfirmationOpen}
                    title="Delete Payment Receipt"
                    message="Are you sure you want to delete this payment receipt?"
                    warningMessage="This action cannot be undone."
                    onConfirm={handleConfirmDeleteZahlungseingang}
                    isLoading={isSavingZahlungseingang}
                    isDangerous={true}
                />

                {/* Delete Payment Amount Confirmation Dialog */}
                <DeleteConfirmationDialog
                    open={deleteZahlungseingangTeilbetragConfirmationOpen}
                    onOpenChange={setDeleteZahlungseingangTeilbetragConfirmationOpen}
                    title="Delete Payment Amount"
                    message="Are you sure you want to remove this partial amount from the payment?"
                    warningMessage="This action cannot be undone."
                    onConfirm={handleConfirmDeleteZahlungseingangTeilbetrag}
                    isDangerous={true}
                />

                {/* Zahlungseingänge Management Drawer */}
                <Drawer
                    open={isZahlungseingaengeDrawerOpen}
                    onOpenChange={(_, { open }) => !open && handleZahlungseingaengeDrawerClose()}
                    position="end"
                    size="large"
                >
                    <DrawerHeader>
                        <DrawerHeaderTitle>
                            Manage Payment Receipts {selectedRechnungForZahlungen && `- Invoice ${selectedRechnungForZahlungen.title || ''}`}
                        </DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody>
                        {zahlungseingaengeParentMessageVisible && (
                            <MessageBar intent={zahlungseingaengeParentMessageType === 'success' ? 'success' : 'error'} style={{ marginBottom: tokens.spacingVerticalM }}>
                                <MessageBarBody>
                                    {zahlungseingaengeParentMessage}
                                </MessageBarBody>
                            </MessageBar>
                        )}
                        {selectedRechnungForZahlungen && (
                            <div className={styles.drawerForm}>
                                {/* Invoice Summary */}
                                <div className={styles.formSection}>
                                    <div className={styles.sectionTitle}>Invoice Information</div>

                                    <div className={styles.formGroup}>
                                        <Label className={styles.formLabel}>Invoice Number</Label>
                                        <div style={{ padding: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 }}>
                                            {selectedRechnungForZahlungen.title || '-'}
                                        </div>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <Label className={styles.formLabel}>Project Partner</Label>
                                        <div style={{ padding: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 }}>
                                            {selectedRechnungForZahlungen.companyTitle || '-'}
                                        </div>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <Label className={styles.formLabel}>Status</Label>
                                        <div style={{ padding: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 }}>
                                            {selectedRechnungForZahlungen.rechnungStatusAsText || '-'}
                                        </div>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <Label className={styles.formLabel}>Amount Net / Gross</Label>
                                        <div style={{ padding: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 }}>
                                            {`${formatEuropeanCurrency(selectedRechnungForZahlungen.betragNetto)} / ${formatEuropeanCurrency(selectedRechnungForZahlungen.betragBrutto)}`}
                                        </div>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <Label className={styles.formLabel}>Open Net / Gross</Label>
                                        <div style={{ padding: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 }}>
                                            {`${formatEuropeanCurrency(selectedRechnungForZahlungen.betragNettoOffen)} / ${formatEuropeanCurrency(selectedRechnungForZahlungen.betragBruttoOffen)}`}
                                        </div>
                                    </div>
                                </div>

                                {/* Payment Receipts Table */}
                                <div className={styles.formSection}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalS }}>
                                        <div className={styles.sectionTitle}>Payment Receipts</div>
                                        <Button
                                            icon={<AddRegular />}
                                            appearance="subtle"
                                            size="small"
                                            disabled={!canEditInvoices}
                                            title={!canEditInvoices ? 'You do not have permission to add payment receipts' : 'Add payment receipt'}
                                            onClick={() => handleAddZahlungseingang(selectedRechnungForZahlungen)}
                                        >
                                            Add Payment Receipt
                                        </Button>
                                    </div>

                                    {selectedRechnungForZahlungen.zahlungen && selectedRechnungForZahlungen.zahlungen.length > 0 ? (
                                        <div className={styles.tableContainer}>
                                            <Table size="small">
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHeaderCell style={{ width: '70px', minWidth: '70px' }}>Actions</TableHeaderCell>
                                                        <TableHeaderCell>Date</TableHeaderCell>
                                                        <TableHeaderCell>Net Amount</TableHeaderCell>
                                                        <TableHeaderCell>Gross Amount</TableHeaderCell>
                                                        <TableHeaderCell>Notes</TableHeaderCell>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {selectedRechnungForZahlungen.zahlungen.map((zahlung: any, index: number) => (
                                                        <TableRow key={index}>
                                                            <TableCell style={{ width: '70px', minWidth: '70px', display: 'flex', gap: '4px' }}>
                                                                <Button
                                                                    icon={<EditRegular />}
                                                                    appearance="subtle"
                                                                    disabled={!canEditInvoices}
                                                                    title={!canEditInvoices ? 'You do not have permission to edit payment receipts' : 'Edit payment receipt'}
                                                                    onClick={() => handleEditZahlungseingang(zahlung)}
                                                                />
                                                                <Button
                                                                    icon={<DeleteRegular />}
                                                                    appearance="subtle"
                                                                    disabled={!canEditInvoices}
                                                                    title={!canEditInvoices ? 'You do not have permission to delete payment receipts' : 'Delete payment receipt'}
                                                                    onClick={() => handleDeleteZahlungseingang(zahlung)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>{zahlung.zeDatumAsText || '-'}</TableCell>
                                                            <TableCell>{formatEuropeanCurrency(zahlung.betragNetto)}</TableCell>
                                                            <TableCell>{formatEuropeanCurrency(zahlung.betragBrutto)}</TableCell>
                                                            <TableCell>{zahlung.bemerkungen || '-'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 }}>
                                            No payment receipts recorded yet. Click "Add Payment Receipt" to add one.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </DrawerBody>

                    <div className={styles.drawerFooter}>
                        <Button onClick={handleZahlungseingaengeDrawerClose} appearance="primary">
                            Close
                        </Button>
                    </div>
                </Drawer>

                {/* Zahlungseingang Add/Edit Drawer */}
                <Drawer
                    open={isZahlungseingangDrawerOpen}
                    onOpenChange={(_, { open }) => !open && handleZahlungseingangDrawerClose()}
                    position="end"
                    size="medium"
                >
                    <DrawerHeader>
                        <DrawerHeaderTitle>
                            {isZahlungseingangEditMode ? 'Edit Payment Receipt' : 'Add Payment Receipt'}
                            {selectedRechnungForZahlungen && ` - Invoice ${selectedRechnungForZahlungen.title || ''}`}
                        </DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody>
                        {selectedRechnungForZahlungen && (
                            <div className={styles.drawerForm}>
                                {/* Payment Receipt Data */}
                                <div className={styles.formSection}>
                                    <div className={styles.sectionTitle}>Payment Receipt Data</div>

                                    <div className={styles.formGroup}>
                                        <Label className={styles.formLabel}>Payment Receipt Date *</Label>
                                        <Input
                                            type="date"
                                            value={zahlungseingangDatum}
                                            onChange={(_, data) => setZahlungseingangDatum(data.value)}
                                            required
                                        />
                                    </div>

                                    <div className={styles.formGroup}>
                                        <Label className={styles.formLabel}>Notes</Label>
                                        <Textarea
                                            value={zahlungseingangBemerkungen}
                                            onChange={(_, data) => setZahlungseingangBemerkungen(data.value || '')}
                                            placeholder="Enter remarks or notes"
                                            rows={4}
                                        />
                                    </div>

                                    <div className={styles.formGroup}>
                                        <Label className={styles.formLabel}>Net Amount</Label>
                                        <div style={{ padding: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 }}>
                                            {formatEuropeanCurrency(parseFloat(zahlungseingangBetrag) || 0)}
                                        </div>
                                    </div>

                                </div>

                                {/* Payment Amounts for Partial Amounts */}
                                <div className={styles.formSection}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalS }}>
                                        <div className={styles.sectionTitle}>Payment Amounts for Partial Amounts</div>
                                        <Button
                                            icon={<AddRegular />}
                                            appearance="subtle"
                                            size="small"
                                            onClick={handleAddZahlungseingangTeilbetrag}
                                            title={!isZahlungseingangEditMode && !editingZahlungseingang?.itemId ? 'First add the Payment Receipt before adding Payment Amounts' : 'Add partial amount to this payment'}
                                            disabled={!isZahlungseingangEditMode && !editingZahlungseingang?.itemId}
                                        >
                                            Add
                                        </Button>
                                    </div>

                                    {!isZahlungseingangEditMode && !editingZahlungseingang?.itemId ? (
                                        <div style={{ color: tokens.colorStatusWarningForeground1, fontSize: tokens.fontSizeBase300, padding: tokens.spacingVerticalM, backgroundColor: tokens.colorStatusWarningBackground1, borderRadius: tokens.borderRadiusMedium }}>
                                            First add the Payment Receipt before adding Payment Amounts
                                        </div>
                                    ) : zahlungseingangTeilbetraege.length === 0 ? (
                                        <div style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 }}>
                                            No partial amounts added yet.
                                        </div>
                                    ) : (
                                        <div className={styles.tableContainer}>
                                            <Table size="small">
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHeaderCell style={{ width: '60px', minWidth: '60px' }}>Actions</TableHeaderCell>
                                                        <TableHeaderCell>Service</TableHeaderCell>
                                                        <TableHeaderCell>Net Amount</TableHeaderCell>
                                                        <TableHeaderCell>Gross Amount</TableHeaderCell>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {zahlungseingangTeilbetraege.map((tb: any, index: number) => {
                                                        const teilbetragItem = selectedRechnungForZahlungen?.teilbetraege?.find(
                                                            (t: any) => t.itemId === tb.teilbetragId
                                                        );
                                                        return (
                                                            <TableRow key={index}>
                                                                <TableCell style={{ width: '60px', minWidth: '60px', display: 'flex', gap: '4px' }}>
                                                                    <Button
                                                                        icon={<EditRegular />}
                                                                        appearance="subtle"
                                                                        size="small"
                                                                        onClick={() => handleEditZahlungseingangTeilbetrag(tb)}
                                                                        title="Edit"
                                                                    />
                                                                    <Button
                                                                        icon={<DeleteRegular />}
                                                                        appearance="subtle"
                                                                        size="small"
                                                                        onClick={() => handleDeleteZahlungseingangTeilbetrag(tb)}
                                                                        title="Delete"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{teilbetragItem?.leistung || '-'}</TableCell>
                                                                <TableCell>{formatEuropeanCurrency(tb.betragNetto)}</TableCell>
                                                                <TableCell>{formatEuropeanCurrency(tb.betragBrutto)}</TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    )}
                                </div>

                                {/* Invoice Summary */}
                                <div className={styles.formSection}>
                                    <div className={styles.sectionTitle}>Invoice Information</div>

                                    <div className={styles.formGroup}>
                                        <Label className={styles.formLabel}>Invoice Number</Label>
                                        <div style={{ padding: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 }}>
                                            {selectedRechnungForZahlungen.title || '-'}
                                        </div>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <Label className={styles.formLabel}>Project Partner</Label>
                                        <div style={{ padding: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 }}>
                                            {selectedRechnungForZahlungen.companyTitle || '-'}
                                        </div>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <Label className={styles.formLabel}>Net / Gross Amount</Label>
                                        <div style={{ padding: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 }}>
                                            {`${formatEuropeanCurrency(selectedRechnungForZahlungen.betragNetto)} / ${formatEuropeanCurrency(selectedRechnungForZahlungen.betragBrutto)}`}
                                        </div>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <Label className={styles.formLabel}>Open Net / Gross Amount</Label>
                                        <div style={{ padding: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 }}>
                                            {`${formatEuropeanCurrency(selectedRechnungForZahlungen.betragNettoOffen)} / ${formatEuropeanCurrency(selectedRechnungForZahlungen.betragBruttoOffen)}`}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </DrawerBody>

                    <div className={styles.drawerFooter}>
                        <Button
                            onClick={handleSaveZahlungseingang}
                            appearance="primary"
                            disabled={!zahlungseingangDatum || isSavingZahlungseingang}
                        >
                            {isSavingZahlungseingang ? 'Saving...' : isZahlungseingangEditMode ? 'Save Changes' : 'Add Payment Receipt'}
                        </Button>
                        <Button onClick={handleZahlungseingangDrawerClose} appearance="secondary">
                            Cancel
                        </Button>
                    </div>
                </Drawer>

                {/* Zahlungseingang Teilbetrag Add/Edit Drawer */}
                <Drawer
                    open={isZahlungseingangTeilbetragDrawerOpen}
                    onOpenChange={(_, { open }) => !open && handleCloseZahlungseingangTeilbetragDrawer()}
                    position="end"
                    size="medium"
                >
                    <DrawerHeader>
                        <DrawerHeaderTitle>
                            {isZahlungseingangTeilbetragEditMode ? 'Edit Payment Amount' : 'Add Payment Amount'}
                        </DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody>
                        {zahlungseingangTeilbetragMessageVisible && (
                            <MessageBar intent={zahlungseingangTeilbetragMessageType === 'success' ? 'success' : 'error'} style={{ marginBottom: tokens.spacingVerticalM }}>
                                <MessageBarBody>
                                    {zahlungseingangTeilbetragMessage}
                                </MessageBarBody>
                            </MessageBar>
                        )}
                        <div className={styles.drawerForm}>
                            <div className={styles.formGroup}>
                                <Label required className={styles.formLabel}>Service</Label>
                                <Combobox
                                    value={
                                        zahlungseingangTeilbetragTeilbetragId
                                            ? selectedRechnungForZahlungen?.teilbetraege?.find((t: any) => t.itemId === zahlungseingangTeilbetragTeilbetragId)?.leistung || ''
                                            : ''
                                    }
                                    onOptionSelect={(_, option) => {
                                        const selected = selectedRechnungForZahlungen?.teilbetraege?.find(
                                            (t: any) => t.leistung === option.optionValue
                                        );
                                        if (selected) {
                                            setZahlungseingangTeilbetragTeilbetragId(selected.itemId);
                                        }
                                    }}
                                >
                                    {selectedRechnungForZahlungen?.teilbetraege?.map((tb: any) => (
                                        <Option key={tb.itemId} value={tb.leistung}>
                                            {tb.leistung}
                                        </Option>
                                    )) || []}
                                </Combobox>
                            </div>

                            {(() => {
                                const selectedTeilbetrag = selectedRechnungForZahlungen?.teilbetraege?.find(
                                    (t: any) => t.itemId === zahlungseingangTeilbetragTeilbetragId
                                );
                                const maxNettoAmount = selectedTeilbetrag?.betragNetto || 0;
                                const maxBruttoAmount = selectedTeilbetrag?.betragBrutto || 0;

                                return (
                                    <>
                                        <div className={styles.formGroup}>
                                            <Label required className={styles.formLabel}>Net Amount</Label>
                                            <Input
                                                type="number"
                                                value={zahlungseingangTeilbetragBetragNetto}
                                                onChange={(_, data) => {
                                                    const value = data.value;
                                                    if (value === '' || parseFloat(value) <= maxNettoAmount) {
                                                        setZahlungseingangTeilbetragBetragNetto(value);
                                                    }
                                                }}
                                                placeholder="0.00"
                                                step="0.01"
                                                min="0"
                                                max={maxNettoAmount.toString()}
                                            />
                                            <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXS }}>
                                                Max: {formatEuropeanCurrency(maxNettoAmount)}
                                            </div>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <Label className={styles.formLabel}>Gross Amount</Label>
                                            <Input
                                                type="number"
                                                value={zahlungseingangTeilbetragBetragBrutto}
                                                onChange={(_, data) => setZahlungseingangTeilbetragBetragBrutto(data.value)}
                                                placeholder="0.00"
                                                step="0.01"
                                                min="0"
                                            />
                                            <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXS }}>
                                                Max: {formatEuropeanCurrency(maxBruttoAmount)}
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </DrawerBody>

                    <div className={styles.drawerFooter}>
                        <Button
                            onClick={handleSaveZahlungseingangTeilbetrag}
                            appearance="primary"
                            disabled={
                                !zahlungseingangTeilbetragTeilbetragId ||
                                !zahlungseingangTeilbetragBetragNetto ||
                                parseFloat(zahlungseingangTeilbetragBetragNetto) >
                                (selectedRechnungForZahlungen?.teilbetraege?.find(
                                    (t: any) => t.itemId === zahlungseingangTeilbetragTeilbetragId
                                )?.betragNetto || 0) ||
                                (zahlungseingangTeilbetragBetragBrutto !== '' && parseFloat(zahlungseingangTeilbetragBetragBrutto) >
                                    (selectedRechnungForZahlungen?.teilbetraege?.find(
                                        (t: any) => t.itemId === zahlungseingangTeilbetragTeilbetragId
                                    )?.betragBrutto || 0))
                            }
                        >
                            {isZahlungseingangTeilbetragEditMode ? 'Save Changes' : 'Add Amount'}
                        </Button>
                        <Button onClick={handleCloseZahlungseingangTeilbetragDrawer} appearance="secondary">
                            Cancel
                        </Button>
                    </div>
                </Drawer>
            </div>
        </>
    );
};

export default Rechnungen;
